(() => {
  "use strict";

  const STORAGE_KEY = "rd-fit-quiz-v3";
  const LEGACY_STORAGE_KEYS = ["rd-fit-quiz-v2", "rd-fit-quiz-v1"];
  const TOTAL_STEPS = 9;
  const EDUCATION_SCREENS = { 3: "whole-prey", 5: "why-raw", 8: "transition" };
  const SAFE_HANDLING = "KEEP FROZEN. THAW IN FRIDGE BEFORE SERVING. DO NOT COOK.";
  const GA4_MEASUREMENT_ID = "G-0FJH8GTQT2";
  const QUESTION_IDS = ["name", "birthday", "breed", "weight", "activity", "raw_status", "proteins", "health", "outcomes"];
  const GA4_EVENT_NAMES = {
    quiz_viewed: "raw_fit_quiz_viewed",
    quiz_intro_view: "raw_fit_quiz_intro_view",
    quiz_started: "raw_fit_quiz_started",
    quiz_step_completed: "raw_fit_quiz_step_completed",
    quiz_completed: "raw_fit_quiz_completed",
    quiz_product_added: "raw_fit_quiz_product_added",
    quiz_plan_saved: "raw_fit_quiz_plan_saved",
    quiz_restarted: "raw_fit_quiz_restarted",
    quiz_email_view: "raw_fit_quiz_email_view",
    quiz_email_submit: "raw_fit_quiz_email_submit",
    quiz_email_skip: "raw_fit_quiz_email_skip",
    quiz_chapter_view: "raw_fit_quiz_chapter_view",
    quiz_answer_edit: "raw_fit_quiz_answer_edit",
    quiz_result_view: "raw_fit_quiz_result_view",
    quiz_guard_ack: "raw_fit_quiz_guard_ack",
    quiz_sms_opt_in: "raw_fit_quiz_sms_opt_in",
  };

  const escapeHtml = (value = "") => String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

  const toArray = (value) => Array.isArray(value) ? value : value ? [value] : [];
  const numberText = (value) => Number.isFinite(Number(value)) ? String(Math.round(Number(value) * 10) / 10) : "—";
  const pounds = (value) => Number.isFinite(Number(value)) ? `${numberText(value)} lb` : "—";
  const moneyFromCents = (value, currency = "USD") => {
    const cents = Number(value);
    if (!Number.isFinite(cents)) return "—";
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  class RawDawgFitQuiz extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.config = this.readJson("[data-rd-fit-config]");
      this.core = window.RawDawgQuizCore;
      this.products = Array.isArray(this.config.products) ? this.config.products : [];
      this.inline = this.dataset.inline === "true";
      this.shell = this.querySelector("[data-rd-fit-shell]");
      this.backdrop = this.querySelector("[data-rd-fit-backdrop]");
      this.stage = this.querySelector("[data-rd-fit-stage]");
      this.footer = this.querySelector("[data-rd-fit-footer]");
      this.backButton = this.querySelector("[data-rd-fit-back]");
      this.nextButton = this.querySelector("[data-rd-fit-next]");
      this.closeButton = this.querySelector("[data-rd-fit-close]");
      this.progressWrap = this.querySelector("[data-rd-fit-progress-wrap]");
      this.progress = this.querySelector("[data-rd-fit-progress]");
      this.progressBar = this.querySelector("[data-rd-fit-progress-bar]");
      this.progressLabel = this.querySelector("[data-rd-fit-progress-label]");
      this.breeds = [];
      this.breedTaxonomy = {};
      this.isOpen = this.inline;
      this.lastFocused = null;

      if (!this.hasCoreContract()) {
        this.renderUnavailable();
        return;
      }

      this.state = this.loadState();
      this.ensureDogId();
      this.saveState();
      this.bindEvents();
      this.loadBreeds();
      if (this.inline) {
        this.renderCurrent({ focus: false });
        this.pushHistory(true);
        this.trackQuizEvent("quiz_viewed");
      }
    }

    hasCoreContract() {
      const required = [
        "SCHEMA_VERSION",
        "createDefaultState",
        "migrateState",
        "validateBirthday",
        "deriveAge",
        "deriveBreedProfile",
        "normalizeExclusiveSelection",
        "evaluateQuestionState",
        "calculateResult",
        "validateCartLines",
        "ensureDogId",
        "ensureCompletionId",
        "rehydrateBreedSelection",
        "buildCompletionPayload",
        "buildPlanEmailPayload",
        "resolveBreedRules",
        "getEffectiveAvoidances",
        "buildTransitionPlan",
      ];
      return Boolean(this.core?.SCHEMA_VERSION) && required.every((name) => name === "SCHEMA_VERSION" || typeof this.core?.[name] === "function");
    }

    renderUnavailable() {
      if (!this.stage) return;
      this.footer?.setAttribute("hidden", "hidden");
      this.stage.innerHTML = `<div class="rd-fit__view"><p class="rd-fit__eyebrow">Raw Fit check</p>${this.viewTitleMarkup("RDFitUnavailableTitle", "rd-fit__heading", "This check is not ready yet.")}<p class="rd-fit__lede">Please refresh in a moment. No answers have been sent or saved outside this browser.</p></div>`;
    }

    readJson(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || "{}");
      } catch (_error) {
        return {};
      }
    }

    defaultState() {
      const state = this.core.createDefaultState();
      return {
        ...state,
        view: "v34_intro",
        currentView: "v34_intro",
        v34EducationSeen: {},
        step: Math.min(TOTAL_STEPS - 1, Math.max(0, Number(state.step) || 0)),
        health: toArray(state.health),
        proteins: toArray(state.proteins),
        outcomes: toArray(state.outcomes).length ? toArray(state.outcomes) : ["none"],
        birthdayPrecision: "approximate_age",
        mealsPerDay: Number(state.mealsPerDay) || 2,
        toleratedProtein: state.toleratedProtein || "no_preference",
      };
    }

    loadState() {
      const parse = (key) => {
        try {
          const raw = sessionStorage.getItem(key);
          return raw ? JSON.parse(raw) : null;
        } catch (_error) {
          return null;
        }
      };

      const stored = parse(STORAGE_KEY);
      const legacy = stored ? null : LEGACY_STORAGE_KEYS.map(parse).find(Boolean);
      const candidate = stored || legacy;
      if (!candidate) return this.defaultState();

      try {
        const migrated = this.core.migrateState(candidate);
        // The removed tolerated-protein field must not silently influence a
        // resumed recommendation. Keep answers, but review avoidances again.
        if (migrated?.toleratedProtein && migrated.toleratedProtein !== "no_preference") {
          const afterQuestion = ["interstitial", "chapter"].includes(migrated.view);
          const resumingQuestion = afterQuestion || ["question", "step", "intro"].includes(migrated.view);
          // A removed interstitial belongs to the question just completed.
          // Preserve its next question before clearing the hidden preference.
          const nextQuestion = (Number(migrated.step) || 0) + (afterQuestion ? 1 : 0);
          migrated.step = resumingQuestion ? Math.min(nextQuestion, 6) : 6;
          migrated.view = "question";
          migrated.currentView = "question";
          delete migrated.returnToEmailAfterEdit;
          delete migrated.returnToResultAfterEdit;
          delete migrated.emailEditStep;
          delete migrated.resultEditStep;
          migrated.result = null;
          migrated.completionId = null;
          migrated.completion_id = null;
          migrated.completionSnapshotAt = null;
          migrated.eventAcknowledgements = {};
          migrated.planRequestAcknowledged = false;
        }
        // Older schemas must reconfirm age at its current question index.
        if (candidate.schemaVersion !== this.core.SCHEMA_VERSION && candidate.schema_version !== this.core.SCHEMA_VERSION) migrated.step = 1;
        if (migrated?.birthdayPrecision === "exact_day" && !migrated.birthday) {
          migrated.birthdayPrecision = "approximate_age";
        }
        return {
          ...this.defaultState(),
          ...(migrated || {}),
          toleratedProtein: "no_preference",
          guardAcknowledged: false,
          guardAcknowledgementFingerprint: null,
        };
      } catch (_error) {
        return this.defaultState();
      }
    }

    saveState() {
      try {
        this.state.schemaVersion = this.core.SCHEMA_VERSION;
        this.state.schema_version = this.core.SCHEMA_VERSION;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (_error) {
        // Browser-local persistence is helpful but never a prerequisite for a safe route.
      }
    }

    invalidateGuardAcknowledgement() {
      this.state.guardAcknowledged = false;
      this.state.guardAcknowledgementFingerprint = null;
    }

    invalidateAnswerDerivedState() {
      this.invalidateGuardAcknowledgement();
      this.state.result = null;
      this.state.completionId = null;
      this.state.completion_id = null;
      this.state.completionSnapshotAt = null;
      this.state.eventAcknowledgements = {};
      this.state.planRequestAcknowledged = false;
      delete this.state.purchaseSelectionFingerprint;
    }

    analyticsProcessingAllowed() {
      try {
        return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
      } catch (_error) {
        return false;
      }
    }

    trackQuizEvent(name, details = {}, onceKey = null) {
      const ga4EventName = GA4_EVENT_NAMES[name];
      if (!ga4EventName) return false;
      if (!this.analyticsProcessingAllowed()) return false;
      this.state = this.state || {};
      this.state.quizAnalyticsEvents = this.state.quizAnalyticsEvents || {};
      if (onceKey && this.state.quizAnalyticsEvents[onceKey]) return false;

      const allowedKeys = new Set([
        "quiz_version", "quiz_mode", "question_number", "question_id", "chapter_id",
        "result_status", "recommendation", "product_key", "purchase_type", "cadence_weeks", "box_count",
        "action", "guarded", "email_step", "sms_opt_in",
      ]);
      const payload = {
        quiz_version: this.config?.engineVersion || this.core?.SCHEMA_VERSION || "unknown",
        quiz_mode: this.inline ? "page" : "modal",
        ...Object.fromEntries(Object.entries(details || {}).filter(([key]) => allowedKeys.has(key))),
      };
      let dispatched = false;

      try {
        if (typeof window.Shopify?.analytics?.publish === "function") {
          const publication = window.Shopify.analytics.publish(`raw_dawg:${name}`, payload);
          publication?.catch?.(() => {});
          dispatched = publication !== false;
        }
      } catch (_error) {
        // GA4 below remains available when Shopify's customer-event bus is not.
      }

      if (typeof window.gtag === "function") {
        try {
          window.gtag("event", ga4EventName, { ...payload, send_to: GA4_MEASUREMENT_ID });
          dispatched = true;
        } catch (_error) {
          // Analytics must never interrupt the quiz.
        }
      }

      if (onceKey && dispatched) {
        this.state.quizAnalyticsEvents[onceKey] = true;
        this.saveState();
      }
      return dispatched;
    }

    applyStateIdentity(value, field) {
      if (typeof value === "string" && value) {
        this.state[field] = value;
      } else if (value && typeof value === "object") {
        this.state = { ...this.state, ...value };
      }
    }

    ensureDogId() {
      try {
        this.applyStateIdentity(this.core.ensureDogId(this.state), "dogId");
      } catch (_error) {
        // The core contract owns IDs. A missing ID leaves provider delivery disabled.
      }
      if (this.state.dog_id && !this.state.dogId) this.state.dogId = this.state.dog_id;
      if (this.state.dogId && !this.state.dog_id) this.state.dog_id = this.state.dogId;
    }

    ensureCompletionId() {
      try {
        this.applyStateIdentity(this.core.ensureCompletionId(this.state), "completionId");
      } catch (_error) {
        // Result delivery stays disabled until the core supplies an idempotency ID.
      }
      if (this.state.completion_id && !this.state.completionId) this.state.completionId = this.state.completion_id;
      if (this.state.completionId && !this.state.completion_id) this.state.completion_id = this.state.completionId;
    }

    bindEvents() {
      document.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-rd-fit-quiz-trigger]");
        if (!trigger || this.inline) return;
        event.preventDefault();
        trigger.closest("details[open]")?.querySelector(":scope > summary")?.click();
        this.open(trigger);
      });

      this.closeButton?.addEventListener("click", () => this.close());
      this.backdrop?.addEventListener("click", (event) => {
        if (event.target === this.backdrop) this.close();
      });
      this.backButton?.addEventListener("click", () => this.goBack());
      this.nextButton?.addEventListener("click", () => this.goNext());
      this.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !this.inline) this.close();
      });
      window.addEventListener("popstate", (event) => this.restoreHistory(event.state));
      document.addEventListener("shopify:section:load", (event) => {
        if (event.target.contains(this) && this.isOpen) this.renderCurrent({ focus: false });
      });
    }

    async loadBreeds() {
      try {
        const breedResponse = await fetch(this.dataset.breedsUrl, { credentials: "same-origin" });
        if (!breedResponse.ok) throw new Error("Breed data is unavailable.");
        const records = await breedResponse.json();
        this.breeds = Array.isArray(records) ? records : [];
      } catch (_error) {
        this.breeds = [];
      }
      try {
        const groupResponse = this.dataset.breedGroupsUrl
          ? await fetch(this.dataset.breedGroupsUrl, { credentials: "same-origin" })
          : null;
        if (groupResponse?.ok) {
          const groups = await groupResponse.json();
          this.breedTaxonomy = groups?.groupByQuizBreedName ? { breedGroups: groups.groupByQuizBreedName } : {};
        } else {
          this.breedTaxonomy = {};
        }
      } catch (_error) {
        this.breedTaxonomy = {};
      }
      this.rehydrateStoredBreed();
      const currentView = this.state.view;
      const currentStep = Number(this.state.step);
      if (this.isOpen && [1, 2, 3].includes(currentStep) && ["question", "step"].includes(currentView)) {
        if (this.stage) {
          const breedInput = this.stage.querySelector?.("[data-rd-breed-input]");
          if (breedInput && this.state.breed?.name) breedInput.value = this.state.breed.name;
          this.syncQuestionState();
        } else if (currentStep === 2) {
          // Defensive fallback for an incompletely mounted instance. Connected
          // quizzes update in place so hydration cannot replace the focused input.
          this.renderCurrent({ focus: false });
        }
      }
      const viewUsesBreedData = currentView === "result"
        || currentView === "email"
        || (["interstitial", "chapter"].includes(currentView) && currentStep === 2);
      if (this.isOpen && viewUsesBreedData) this.renderCurrent({ focus: false });
    }

    open(trigger) {
      this.lastFocused = trigger || document.activeElement;
      this.isOpen = true;
      this.backdrop.hidden = false;
      this.shell.hidden = false;
      document.body.classList.add("rd-fit-open");
      this.renderCurrent();
      this.pushHistory();
      this.trackQuizEvent("quiz_viewed");
      requestAnimationFrame(() => {
        const focusTarget = this.stage.querySelector("[data-rd-fit-view-title]")
          || this.shell.querySelector("button, input, select, a");
        if (typeof trapFocus === "function") trapFocus(this.shell, focusTarget);
        focusTarget?.focus({ preventScroll: true });
      });
    }

    close() {
      this.isOpen = false;
      this.backdrop.hidden = true;
      this.shell.hidden = true;
      document.body.classList.remove("rd-fit-open");
      if (typeof removeTrapFocus === "function") removeTrapFocus(this.lastFocused);
      else this.lastFocused?.focus();
    }

    start() {
      this.state.view = "question";
      this.state.currentView = "question";
      this.state.step = 0;
      this.trackQuizEvent("quiz_started", {}, "quiz_started");
      this.saveState();
      this.pushHistory();
      this.renderCurrent();
    }

    historyState() {
      return { rdFit: true, view: this.state.view, step: this.state.step };
    }

    historyHash() {
      if (this.state.view === "email") return "#raw-fit-email";
      if (this.state.view === "result") return "#raw-fit-result";
      if (this.state.view === "question") return `#raw-fit-question-${this.state.step + 1}`;
      if (this.state.view === "v34_intro") return "#raw-fit-intro";
      if (this.state.view === "v34_education") return `#raw-fit-${EDUCATION_SCREENS[this.state.step] || "education"}`;
      return "#raw-fit";
    }

    pushHistory(replace = false) {
      if (!window.history?.pushState) return;
      const method = replace ? "replaceState" : "pushState";
      if (window.location.hash !== this.historyHash() || !window.history.state?.rdFit) {
        history[method](this.historyState(), "", this.historyHash());
      }
    }

    enforceBodyConditionReconfirmation(force = false) {
      if (this.state.bodyConditionReconfirmationPending !== true) return false;
      const { view } = this.state;
      const step = Number(this.state.step) || 0;
      const beforeConfirmation = ["intro", "v34_intro"].includes(view)
        || (["question", "step"].includes(view) && step <= 3)
        || (["interstitial", "chapter"].includes(view) && step < 3);
      if (!force && beforeConfirmation) return false;
      this.state.view = "question";
      this.state.currentView = "question";
      this.state.step = 3;
      return true;
    }

    restoreHistory(historyState) {
      if (!historyState?.rdFit || !this.isOpen) return;
      this.state.view = historyState.view;
      this.state.currentView = historyState.view;
      this.state.step = Math.min(TOTAL_STEPS - 1, Math.max(0, Number(historyState.step) || 0));
      this.enforceBodyConditionReconfirmation();
      this.saveState();
      this.renderCurrent();
    }

    renderCurrent({ focus = true } = {}) {
      if (!this.isOpen || !this.stage) return;
      // Resume old saved screens/history without showing a removed splash or note.
      if (this.state.view === "intro") {
        this.state.step = 0;
        this.state.view = "question";
      } else if (["interstitial", "chapter"].includes(this.state.view)) {
        if (this.state.step < TOTAL_STEPS - 1) {
          this.state.step += 1;
          this.state.view = "question";
        } else {
          this.state.view = this.state.result ? "email" : "question";
        }
      }
      if (!["question", "email", "result", "v34_intro", "v34_education"].includes(this.state.view)) this.state.view = "question";
      this.enforceBodyConditionReconfirmation();
      this.state.currentView = this.state.view;
      this.saveState();
      if (this.nextButton) { this.nextButton.disabled = false; this.nextButton.hidden = false; }
      this.setButtonLabel(this.backButton?.querySelector("span"), this.config.labels?.back || "Back");
      if (this.state.view === "result") this.renderResult({ focus });
      else if (this.state.view === "email") this.renderEmailStep({ focus });
      else if (["v34_intro", "v34_education"].includes(this.state.view)) this.renderEducation({ focus });
      else this.renderQuestion({ focus });
      this.dataset.view = this.state.view;
      this.resetViewScroll({ focus });
    }

    resetViewScroll({ focus = true } = {}) {
      this.stage.scrollTop = 0;
      if (this.inline && focus) this.scrollIntoView?.({ block: "start", behavior: "auto" });
    }

    focusAfterRender(selector) {
      requestAnimationFrame(() => {
        // A rendered view title is the default target after a dynamic stage
        // replacement. Search the component as a fallback for persistent UI.
        const target = this.stage.querySelector(selector) || this.querySelector(selector) || this.stage;
        target?.focus({ preventScroll: true });
      });
    }

    tokenText(value) {
      const dogName = String(this.state.name || "your dog").trim() || "your dog";
      const selectedBreed = String(this.state.breed?.name || "").trim();
      const breed = selectedBreed === "Mixed Breed"
        ? "mixed-breed dogs"
        : selectedBreed === "Unknown"
          ? "dogs of an unknown breed"
          : selectedBreed || "your dog's breed";
      return String(value || "")
        .replaceAll("[dog_name]", dogName)
        .replaceAll("[Breed]", breed)
        .replaceAll("{{name}}", dogName);
    }

    replaceTokens(value) {
      return escapeHtml(this.tokenText(value));
    }

    questionText(key) {
      return this.replaceTokens(this.config.questions?.[key] || "");
    }

    viewTitleTag() {
      return this.inline ? "h1" : "h2";
    }

    viewTitleMarkup(id, className, content) {
      const tag = this.viewTitleTag();
      return `<${tag} class="${className}" id="${id}" data-rd-fit-view-title tabindex="-1">${content}</${tag}>`;
    }

    setButtonLabel(button, text) {
      const label = button?.querySelector?.(".button__label") || button;
      if (!label || !text) return;
      text = this.tokenText(text);
      label.textContent = text;
      label.dataset.label = text;
      const control = label.closest?.("button, a") || button;
      control?.setAttribute?.("aria-label", text);
    }

    questionHeader(key, note = "") {
      return `<header class="rd-fit__question-head"><p class="rd-fit__eyebrow">${this.sectionLabel()}</p>${this.viewTitleMarkup("RDFitQuestionTitle", "rd-fit__question-title", this.questionText(key))}${note ? `<p class="rd-fit__question-note">${this.replaceTokens(note)}</p>` : ""}</header>`;
    }

    renderQuestion({ focus = true } = {}) {
      this.state.view = "question";
      this.state.currentView = "question";
      this.footer.hidden = false;
      this.progressWrap.hidden = false;
      this.backButton.hidden = false;
      this.updateProgress(`Question ${this.state.step + 1} of ${TOTAL_STEPS}`);
      const renderers = [
        () => this.nameQuestion(),
        () => this.birthdayQuestion(),
        () => this.breedQuestion(),
        () => this.weightQuestion(),
        () => this.activityQuestion(),
        () => this.rawStatusQuestion(),
        () => this.proteinQuestion(),
        () => this.healthQuestion(),
        () => this.outcomeQuestion(),
      ];
      this.stage.innerHTML = `<div class="rd-fit__view">${renderers[this.state.step]()}</div>`;
      this.bindQuestionControls();
      this.setButtonLabel(this.nextButton, this.config.labels?.next || "Continue");
      this.syncQuestionState();
      if (focus) this.focusAfterRender("[data-rd-fit-view-title]");
    }

    sectionLabel() {
      return this.state.step < 4 ? "The Dawg" : this.state.step < 6 ? "The Routine" : this.state.step < 8 ? "The Screen" : "The Goal";
    }

    updateProgress() {
      const current = this.state.step + 1;
      const education = this.state.view === "v34_education";
      const completed = education ? current : this.state.step;
      this.progress?.setAttribute("aria-valuemin", "0");
      this.progress?.setAttribute("aria-valuenow", String(completed));
      this.progress?.setAttribute("aria-valuetext", education ? `${completed} of ${TOTAL_STEPS} questions completed` : `Question ${current} of ${TOTAL_STEPS}`);
      if (this.progressLabel) this.progressLabel.textContent = education ? "Your feeding guide" : this.sectionLabel();
      if (this.progressBar) this.progressBar.style.width = `${(completed / TOTAL_STEPS) * 100}%`;
    }

    breedNotices() {
      if (!this.state.breed?.name || !this.core.resolveBreedRules) return [];
      return this.core.resolveBreedRules(this.breedProfile()).notices || [];
    }

    breedNoticeMarkup(notices = this.breedNotices()) {
      return notices.map((notice) => `<aside class="rd-fit__breed-notice" data-rd-fit-breed-notice data-notice-id="${escapeHtml(notice.id)}"><h3>${escapeHtml(notice.title)}</h3><p>${escapeHtml(notice.message)}</p></aside>`).join("");
    }

    updateBreedNotice() {
      const slot = this.stage.querySelector("[data-rd-fit-breed-notices]");
      if (slot) slot.innerHTML = this.breedNoticeMarkup();
    }

    effectiveAvoidances(result = {}) {
      return this.core.getEffectiveAvoidances(this.state.proteins || [], result);
    }

    transitionMarkup() {
      const plan = this.core.buildTransitionPlan(this.state);
      return `<section class="rd-fit__transition" data-rd-fit-transition="${escapeHtml(plan.kind)}"><h3>${this.replaceTokens(plan.title)}</h3><p>${this.replaceTokens(plan.message)}</p>${plan.stages?.length ? `<ol class="rd-fit__formula-tiles">${plan.stages.map((stage) => `<li><strong>${escapeHtml(stage.percent)}%</strong><span>${escapeHtml(stage.days)}</span></li>`).join("")}</ol>` : ""}<p class="rd-fit__inline-note">${SAFE_HANDLING} Wash hands, bowls and preparation surfaces after handling raw food.</p></section>`;
    }

    renderEducation({ focus = true } = {}) {
      const intro = this.state.view === "v34_intro";
      const screen = intro ? "intro" : EDUCATION_SCREENS[this.state.step];
      if (!screen) { this.state.view = "question"; return this.renderCurrent({ focus }); }
      this.footer.hidden = false;
      this.backButton.hidden = intro;
      this.progressWrap.hidden = intro;
      if (!intro) this.updateProgress();
      const headings = {
        intro: this.config.education?.introHeading || "Find your dawg’s raw starting point.",
        "whole-prey": "Built from the animal out.",
        "why-raw": "Know what goes in [dog_name]’s bowl.",
        transition: "A gradual start for [dog_name].",
      };
      const content = intro
        ? `<p class="rd-fit__lede">Nine questions for a daily portion estimate, a blend screened against our breed rules and your protein avoidances, and transition guidance.</p><ul class="rd-fit__intro-proof"><li><strong>A measured starting portion</strong><span>Based on weight, body condition and activity.</span></li><li><strong>Clear ingredient choices</strong><span>Breed notices and protein exclusions stay with your plan.</span></li><li><strong>A guide for the first bowl</strong><span>Review the plan before you order.</span></li></ul><p class="rd-fit__inline-note">Nine questions, at your own pace. You can view your results without sharing an email address.</p>`
        : screen === "whole-prey"
          ? `<p class="rd-fit__lede">Our whole-prey formula starts with four animal-based components.</p><ul class="rd-fit__formula-tiles"><li><strong>75%</strong><span>Muscle meat</span></li><li><strong>10%</strong><span>Raw meaty bone</span></li><li><strong>10%</strong><span>Organs</span></li><li><strong>5%</strong><span>Animal-based components</span></li></ul><p>The final 5% varies by recipe; green tripe and connective tissue are part of the Beef blend. Check each blend’s ingredient list against [dog_name]’s needs. A formula ratio alone does not establish individual suitability.</p>`
          : screen === "why-raw"
            ? `<p class="rd-fit__lede">Raw Dawg brings meat, bone and organs together in a frozen raw blend. Each recipe has its own ingredients; the next questions help us screen those against the proteins [dog_name] needs to avoid.</p><p>Raw feeding is a choice to make around your dog’s needs and your household’s handling routine. This quiz estimates portions and screens ingredients; it does not replace veterinary advice.</p>`
            : this.transitionMarkup();
      this.stage.innerHTML = `<div class="rd-fit__view rd-fit__education" data-rd-fit-screen="${screen}"><p class="rd-fit__eyebrow">${intro ? "Raw Fit" : screen === "whole-prey" ? "The whole-prey formula" : screen === "why-raw" ? "Why raw" : "Your starting routine"}</p>${this.viewTitleMarkup("RDFitEducationTitle", "rd-fit__heading", this.replaceTokens(headings[screen]))}${intro || screen === "transition" ? content : content.replaceAll("[dog_name]", this.replaceTokens("[dog_name]"))}</div>`;
      this.setButtonLabel(this.nextButton, intro ? this.config.education?.startLabel || "Start the Raw Fit Check" : screen === "transition" ? "See [dog_name]’s plan" : screen === "why-raw" ? "Build [dog_name]’s bowl" : "Keep going");
      this.trackQuizEvent(intro ? "quiz_intro_view" : "quiz_chapter_view", { chapter_id: screen, action: "first_view" }, `v34_screen_${screen}`);
      if (focus) this.focusAfterRender("[data-rd-fit-view-title]");
    }

    errorMarkup() {
      return '<p class="rd-fit__error" id="RDFitQuestionError" role="alert" data-rd-fit-error hidden></p>';
    }

    requiredHelper(region) {
      return `<p class="rd-fit__required-helper" id="RDFitRequired-${escapeHtml(region)}" data-rd-fit-required-helper="${escapeHtml(region)}" hidden><span class="rd-fit__required-icon" aria-hidden="true">!</span><span>Complete this to continue</span></p>`;
    }

    advisorySlot() {
      return '<div class="rd-fit__advisory" data-rd-fit-advisory-slot role="status" aria-live="polite" aria-atomic="true" hidden></div>';
    }

    questionEvaluation(drafts = {}) {
      const evaluation = this.core.evaluateQuestionState(this.state, this.state.step, {
        breedData: this.breeds,
        groupData: this.breedTaxonomy,
        asOf: this.todayISO(),
        drafts,
      });
      if (this.state.step === 1 && this.state.birthdayPrecision === "approximate_age") {
        const age = this.ageInputValue();
        return {
          ...evaluation,
          target: age.target || "#RDFitAgeYears",
          ...(!age.valid ? { complete: false, state: age.missing ? "missing" : "invalid", region: "birthday", message: age.message } : {}),
        };
      }
      return evaluation;
    }

    ageInputValue() {
      for (const id of ["RDFitAgeYears", "RDFitAgeMonths"]) {
        if (this.stage?.querySelector?.(`#${id}`)?.validity?.badInput) {
          return { valid: false, target: `#${id}`, message: "Enter a whole number." };
        }
      }
      const years = String(this.state.ageYearsInput ?? "").trim();
      const months = String(this.state.ageMonthsInput ?? "").trim();
      if (!years && !months) return { valid: false, missing: true, message: "Enter an age, or choose Not sure." };
      if (years && (!/^\d+$/.test(years) || Number(years) > 30)) {
        return { valid: false, target: "#RDFitAgeYears", message: "Enter whole years from 0 to 30." };
      }
      if (months && (!/^\d+$/.test(months) || Number(months) > 11)) {
        return { valid: false, target: "#RDFitAgeMonths", message: "Enter whole months from 0 to 11." };
      }
      const total = Number(years || 0) * 12 + Number(months || 0);
      if (total > 360) return { valid: false, target: "#RDFitAgeYears", message: "Enter an age up to 30 years." };
      return { valid: true, months: total };
    }

    clearQuestionStateUi() {
      this.stage.querySelectorAll?.("[data-rd-fit-required-region]").forEach((region) => {
        region.classList.toggle?.("is-incomplete", false);
      });
      this.stage.querySelectorAll?.("[data-rd-fit-required-helper]").forEach((helper) => {
        helper.hidden = true;
      });
      this.stage.querySelectorAll?.('[aria-invalid="true"], [aria-describedby]').forEach((control) => {
        const describedBy = String(control.getAttribute?.("aria-describedby") || "").split(/\s+/).filter(Boolean);
        if (describedBy.some((id) => id === "RDFitQuestionError" || id.startsWith("RDFitRequired-"))) {
          const retained = describedBy.filter((id) => id !== "RDFitQuestionError" && !id.startsWith("RDFitRequired-"));
          control.removeAttribute("aria-invalid");
          if (retained.length) control.setAttribute("aria-describedby", retained.join(" "));
          else control.removeAttribute("aria-describedby");
        }
      });
      const error = this.stage.querySelector?.("[data-rd-fit-error]");
      if (error) {
        error.textContent = "";
        error.hidden = true;
      }
    }

    advisoryTitle(level) {
      return {
        pending: "Breed will confirm",
        wait: "Pause raw for now",
        conditional: "Confirm before feeding",
      }[level] || "";
    }

    updateQuestionAdvisory(evaluation) {
      const slot = this.stage.querySelector?.("[data-rd-fit-advisory-slot]");
      const advisories = toArray(evaluation?.advisories).filter((advisory) => advisory?.body);
      const level = String(evaluation?.highestSafetyLevel || "");
      const inlineSlots = [...(this.stage.querySelectorAll?.("[data-rd-fit-inline-advisory]") || [])];
      const currentControlSlots = [...(this.stage.querySelectorAll?.("[data-rd-fit-inline-advisory-current]") || [])];
      if (level === "estimate") {
        [...inlineSlots, ...currentControlSlots].forEach((inline) => {
          inline.hidden = true;
          inline.innerHTML = "";
          delete inline.dataset.level;
        });
        if (slot) {
          slot.dataset.level = "estimate";
          slot.innerHTML = `<p class="rd-fit__inline-note">${escapeHtml(advisories[0]?.body || "")}</p>`;
          slot.hidden = !advisories.length;
        }
        return;
      }
      const assigned = new Set();
      const renderInline = (inline, advisory) => {
        if (!advisory) {
          inline.hidden = true;
          inline.innerHTML = "";
          delete inline.dataset.level;
          return;
        }
        const markup = `<span class="rd-fit__choice-advisory-icon" aria-hidden="true">!</span><span><strong>${escapeHtml(advisory.title || this.advisoryTitle(advisory.level))}</strong><span>${escapeHtml(advisory.body)}</span></span>`;
        inline.dataset.level = advisory.level;
        if (inline.innerHTML !== markup) inline.innerHTML = markup;
        inline.hidden = false;
      };
      inlineSlots.forEach((inline) => {
        const advisory = advisories.find((candidate) => (
          String(candidate.targetValue || "") === String(inline.dataset.rdFitInlineAdvisory || "")
        ));
        if (advisory) assigned.add(advisory);
        renderInline(inline, advisory || null);
      });
      currentControlSlots.forEach((inline, index) => {
        const advisory = advisories.filter((candidate) => !assigned.has(candidate))[index] || null;
        if (advisory) assigned.add(advisory);
        renderInline(inline, advisory);
      });
      if (!slot) return;
      if (!level || !advisories.length) {
        slot.hidden = true;
        slot.innerHTML = "";
        delete slot.dataset.level;
        return;
      }
      const title = this.advisoryTitle(level);
      const dogName = String(this.state.name || "your dawg").trim() || "your dawg";
      const summary = {
        pending: "Age guidance is provisional until breed and size confirm the adult window.",
        wait: "At least one answer means raw should pause for now.",
        conditional: "At least one answer needs confirmation before feeding.",
      }[level] || "Review the notes above before feeding.";
      slot.dataset.level = level;
      const markup = `<span class="rd-fit__advisory-icon" aria-hidden="true">!</span><div class="rd-fit__advisory-copy"><p class="rd-fit__advisory-label">Safety note</p><h3>${escapeHtml(title)}</h3><p>${escapeHtml(summary)}</p><p class="rd-fit__advisory-next">You can change this answer or continue. We’ll still build ${escapeHtml(dogName)}’s future blend and feeding plan.</p></div>`;
      if (slot.innerHTML !== markup) slot.innerHTML = markup;
      slot.hidden = false;
    }

    syncQuestionState({ drafts = {} } = {}) {
      const scrollOwner = this.stage;
      const stageScrollTop = Number(scrollOwner?.scrollTop || 0);
      const windowScrollX = Number(window.scrollX || 0);
      const windowScrollY = Number(window.scrollY || 0);
      const restoreScroll = () => {
        if (scrollOwner && Math.abs(scrollOwner.scrollTop - stageScrollTop) > 1) scrollOwner.scrollTop = stageScrollTop;
        if (Math.abs(window.scrollY - windowScrollY) > 1) window.scrollTo(windowScrollX, windowScrollY);
      };
      const evaluation = this.questionEvaluation(drafts);
      this.clearQuestionStateUi();
      if (this.nextButton) this.nextButton.disabled = !evaluation.complete;

      if (!evaluation.complete && evaluation.region) {
        const regionSelector = String(evaluation.region).startsWith("[")
          ? evaluation.region
          : `[data-rd-fit-required-region="${evaluation.region}"]`;
        const region = this.stage.querySelector?.(regionSelector);
        const regionKey = region?.dataset?.rdFitRequiredRegion
          || String(evaluation.region).match(/data-rd-fit-required-region=["']([^"']+)/)?.[1]
          || evaluation.region;
        const helper = this.stage.querySelector?.(`[data-rd-fit-required-helper="${regionKey}"]`);
        region?.classList.toggle?.("is-incomplete", true);
        if (helper) helper.hidden = false;

        const target = evaluation.target ? this.stage.querySelector?.(evaluation.target) : null;
        if (target) {
          const describedBy = String(target.getAttribute?.("aria-describedby") || "")
            .split(/\s+/)
            .filter((id) => id && id !== "RDFitQuestionError" && !id.startsWith("RDFitRequired-"));
          describedBy.push(`RDFitRequired-${regionKey}`);
          if (evaluation.state === "invalid") {
            const error = this.stage.querySelector?.("[data-rd-fit-error]");
            target.setAttribute("aria-invalid", "true");
            describedBy.push("RDFitQuestionError");
            if (error) {
              error.textContent = evaluation.message || "Check this answer before continuing.";
              error.hidden = false;
            }
          }
          target.setAttribute("aria-describedby", describedBy.join(" "));
        }
      }

      this.updateQuestionAdvisory(evaluation);
      if (this.state.step === 2) this.updateBreedNotice();
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      return evaluation;
    }

    nameQuestion() {
      return `${this.questionHeader("name", "A few quick questions for a feeding estimate.")}
        <div class="rd-fit__required-region" data-rd-fit-required-region="name"><div class="rd-fit__field">
          <label for="RDFitDogName">Dawg's name</label>
          <input class="rd-fit__input rd-fit__input--name" id="RDFitDogName" type="text" maxlength="40" autocomplete="off" value="${escapeHtml(this.state.name || "")}" data-field="name" placeholder="Bojangles">
        </div>${this.requiredHelper("name")}</div><p class="rd-fit__birthday-help">Your answers stay in this browser unless you choose to send your plan.</p>${this.errorMarkup()}`;
    }

    specialBreeds() {
      return [
        { name: "Mixed Breed", kind: "mixed" },
        { name: "Unknown", kind: "unknown" },
      ];
    }

    breedQuestion() {
      const current = this.state.breed?.name || "";
      return `${this.questionHeader("breed", "Search by breed, or choose Mixed Breed or Unknown.")}
        <div class="rd-fit__required-region" data-rd-fit-required-region="breed"><div class="rd-fit__field rd-fit__combobox" data-rd-fit-combobox>
          <label for="RDFitBreed">Breed</label>
          <input class="rd-fit__input" id="RDFitBreed" type="search" role="combobox" aria-autocomplete="list" aria-controls="RDFitBreedList" aria-expanded="false" autocomplete="off" value="${escapeHtml(current)}" data-rd-breed-input placeholder="Start typing a breed">
          <ul class="rd-fit__listbox" id="RDFitBreedList" role="listbox" data-rd-breed-list hidden></ul>
        </div>${this.requiredHelper("breed")}<div class="rd-fit__choice-advisory" data-rd-fit-inline-advisory-current hidden></div></div>${this.advisorySlot()}<div data-rd-fit-breed-notices aria-live="polite">${this.breedNoticeMarkup()}</div>${this.errorMarkup()}`;
    }

    birthdayQuestion() {
      const precision = this.state.birthdayPrecision || "approximate_age";
      if (this.state.ageYearsInput == null && this.state.ageMonthsInput == null && this.state.estimatedAgeMonths != null) {
        this.state.ageYearsInput = String(Math.floor(Number(this.state.estimatedAgeMonths) / 12));
        this.state.ageMonthsInput = String(Number(this.state.estimatedAgeMonths) % 12);
      }
      const choices = [
        ["approximate_age", "Age estimate", ""],
        ["exact_day", "Exact birthday (optional)", ""],
        ["unknown", "Not sure", ""],
      ];
      // Keep a previously saved month/year truthful until the visitor changes it.
      if (precision === "month_only") choices.push(["month_only", "Saved month & year", ""]);
      const ageFields = precision === "approximate_age" ? `<div class="rd-fit__age-fields"><div class="rd-fit__field"><label for="RDFitAgeYears">Years</label><input class="rd-fit__input" id="RDFitAgeYears" type="number" inputmode="numeric" min="0" max="30" step="1" value="${escapeHtml(this.state.ageYearsInput ?? "")}" data-age-part="ageYearsInput" placeholder="0"></div><div class="rd-fit__field"><label for="RDFitAgeMonths">Months</label><input class="rd-fit__input" id="RDFitAgeMonths" type="number" inputmode="numeric" min="0" max="11" step="1" value="${escapeHtml(this.state.ageMonthsInput ?? "")}" data-age-part="ageMonthsInput" placeholder="0"></div></div>` : "";
      const dateField = precision === "exact_day" ? `<div class="rd-fit__field"><label for="RDFitBirthday">Birthday</label><input class="rd-fit__input" id="RDFitBirthday" type="date" max="${this.todayISO()}" value="${escapeHtml(this.state.birthday || "")}" data-field="birthday"></div>` : "";
      const monthField = precision === "month_only" ? `<div class="rd-fit__field"><label for="RDFitBirthdayMonth">Saved birthday month and year</label><input class="rd-fit__input" id="RDFitBirthdayMonth" type="month" max="${this.todayISO().slice(0, 7)}" value="${escapeHtml(this.state.birthday || "")}" data-field="birthday"></div>` : "";
      return `${this.questionHeader("birthday", "An estimate is fine. Add an exact birthday only if you know it.")}
        <div class="rd-fit__required-region" data-rd-fit-required-region="birthday"><fieldset class="rd-fit__birthday-precision" role="radiogroup"><legend class="visually-hidden">How would you like to enter their age?</legend>${this.choiceButtons(choices, precision, "birthdayPrecision")}</fieldset>
        ${ageFields}${dateField}${monthField}
        ${this.requiredHelper("birthday")}</div>${this.advisorySlot()}${this.errorMarkup()}`;
    }

    weightQuestion() {
      const conditions = [
        ["underweight", "Underweight", ""],
        ["ideal", "Ideal", ""],
        ["overweight", "Overweight", ""],
      ];
      return `${this.questionHeader("weight", "Use their current weight to start.")}
        <div class="rd-fit__required-region" data-rd-fit-required-region="currentWeight"><div class="rd-fit__field"><label for="RDFitWeight">Current weight (lb)</label><input class="rd-fit__input" id="RDFitWeight" type="number" inputmode="decimal" min="2" max="250" step="0.1" value="${escapeHtml(this.state.currentWeight || "")}" data-field="currentWeight"></div>${this.requiredHelper("currentWeight")}<div class="rd-fit__choice-advisory" data-rd-fit-inline-advisory-current hidden></div></div>
        <div class="rd-fit__required-region" data-rd-fit-required-region="bodyCondition"><fieldset role="radiogroup" class="rd-fit__choices rd-fit__choices--three" style="margin-top:18px" aria-describedby="RDFitBodyConditionHelp"><legend class="rd-fit__legend">Body condition</legend>${this.choiceButtons(conditions, this.state.bodyCondition || "", "bodyCondition")}</fieldset><p class="rd-fit__inline-note" id="RDFitBodyConditionHelp">Ideal means ribs are easy to feel with a light touch and a visible waist from above. Choose the description that fits now.</p>${this.requiredHelper("bodyCondition")}</div>
        ${this.advisorySlot()}${this.errorMarkup()}`;
    }

    activityQuestion() {
      const choices = [
        ["low", "Low", "Short walks and plenty of rest."],
        ["moderate", "Moderate", "Daily walks and play."],
        ["high", "High", "Long runs, work, or sport."],
      ];
      return `${this.questionHeader("activity")}
        <div class="rd-fit__required-region" data-rd-fit-required-region="activity"><fieldset role="radiogroup" class="rd-fit__choices rd-fit__choices--three"><legend class="visually-hidden">Activity level</legend>${this.choiceButtons(choices, this.state.activity, "activity")}</fieldset>${this.requiredHelper("activity")}</div>${this.errorMarkup()}`;
    }

    rawStatusQuestion() {
      const choices = [
        ["regularly", "Regularly", "Raw is part of their routine."],
        ["sometimes", "Sometimes or as a topper", "Some raw alongside other food."],
        ["not_yet", "Not yet", "New to raw."],
      ];
      return `${this.questionHeader("rawStatus")}
        <fieldset role="radiogroup" class="rd-fit__choices rd-fit__choices--three"><legend class="visually-hidden">Current raw feeding</legend>${this.choiceButtons(choices, this.state.rawStatus || "not_yet", "rawStatus")}</fieldset>${this.errorMarkup()}`;
    }

    proteinQuestion() {
      const avoidances = [
        ["none", "No avoidances", ""],
        ["beef", "Beef", ""],
        ["turkey", "Turkey", ""],
        ["salmon", "Salmon", ""],
      ];
      return `${this.questionHeader("proteins", "Select all that apply, or choose No avoidances.")}
        <div class="rd-fit__required-region" data-rd-fit-required-region="proteins"><fieldset class="rd-fit__choices rd-fit__choices--two"><legend class="visually-hidden">Proteins to avoid</legend>${this.choiceButtons(avoidances, this.state.proteins, "proteins", true)}</fieldset>${this.requiredHelper("proteins")}</div>${this.errorMarkup()}`;
    }

    healthQuestion() {
      const categoryChoices = [
        ["none", "None of these apply", ""],
        ["antibiotics_recent", "Antibiotics — taking now or finished within 14 days", ""],
        ["immunocompromised", "Immune disorder or current immunosuppression", ""],
        ["pregnant", "Pregnant", ""],
        ["nursing", "Nursing", ""],
      ];
      const pancreatitisTiming = [
        ["pancreatitis_active", "Current or recovering", ""],
        ["pancreatitis_stable", "History, currently stable", ""],
      ];
      const giTiming = [
        ["gi_active", "Current or recovering", ""],
        ["gi_stable", "History, currently stable", ""],
      ];
      const selectedHealth = toArray(this.state.health);
      return `${this.questionHeader("health", "Select all that apply.")}
        <div class="rd-fit__required-region" data-rd-fit-required-region="health"><fieldset class="rd-fit__choices rd-fit__choices--two"><legend class="rd-fit__legend">Current or recent status</legend>${this.choiceButtons(categoryChoices, selectedHealth, "health", true, true)}</fieldset>
        <div class="rd-fit__condition-grid">
          <fieldset class="rd-fit__condition-card"><legend>Pancreatitis</legend><p>Choose the timing that applies.</p><div class="rd-fit__choices rd-fit__choices--two" role="radiogroup" aria-label="Pancreatitis timing">${this.choiceButtons(pancreatitisTiming, selectedHealth.find((value) => value.startsWith("pancreatitis_")) || "", "healthPancreatitisTiming", false, true)}</div><button class="rd-fit__condition-clear${selectedHealth.some((value) => value.startsWith("pancreatitis_")) ? "" : " is-disabled"}" type="button" data-rd-fit-clear-condition="pancreatitis" aria-disabled="${!selectedHealth.some((value) => value.startsWith("pancreatitis_"))}">Clear pancreatitis selection</button></fieldset>
          <fieldset class="rd-fit__condition-card"><legend>GI disease or IBD</legend><p>Choose the timing that applies.</p><div class="rd-fit__choices rd-fit__choices--two" role="radiogroup" aria-label="GI disease or IBD timing">${this.choiceButtons(giTiming, selectedHealth.find((value) => value.startsWith("gi_")) || "", "healthGiTiming", false, true)}</div><button class="rd-fit__condition-clear${selectedHealth.some((value) => value.startsWith("gi_")) ? "" : " is-disabled"}" type="button" data-rd-fit-clear-condition="gi" aria-disabled="${!selectedHealth.some((value) => value.startsWith("gi_"))}">Clear GI selection</button></fieldset>
        </div>${this.requiredHelper("health")}</div>${this.advisorySlot()}${this.errorMarkup()}`;
    }

    outcomeQuestion() {
      const choices = [
        ["digestion", "Digestion", "Keep the transition gradual and the starting portion measured."],
        ["coat", "Skin and coat support", "Tell us what you want to focus on."],
        ["energy", "Steady energy", "A measured portion for their daily routine."],
        ["interest", "More mealtime interest", "Make time for a consistent mealtime routine."],
        ["none", "No single priority", "Start with a portion and ingredient screen."],
      ];
      return `${this.questionHeader("outcomes", "Choose one priority.")}
        <fieldset role="radiogroup" class="rd-fit__choices rd-fit__choices--two"><legend class="visually-hidden">Primary desired outcome</legend>${this.choiceButtons(choices, this.state.primaryOutcome || toArray(this.state.outcomes)[0] || "none", "primaryOutcome")}</fieldset>${this.errorMarkup()}`;
    }

    choiceButtons(choices, selected, field, multiple = false, advisorySlots = false) {
      const values = toArray(selected);
      return choices.map(([value, title, text], index) => {
        const checked = values.includes(value);
        const tabIndex = multiple || checked || (!values.length && index === 0) ? 0 : -1;
        const button = `<button class="rd-fit__choice${checked ? " is-selected" : ""}" type="button" role="${multiple ? "checkbox" : "radio"}" aria-checked="${checked}" tabindex="${tabIndex}" data-rd-fit-choice data-choice-field="${escapeHtml(field)}" data-choice-value="${escapeHtml(value)}" data-multiple="${multiple}"><span class="rd-fit__choice-mark" aria-hidden="true">✓</span><span class="rd-fit__choice-copy"><strong>${escapeHtml(title)}</strong>${text ? `<span>${escapeHtml(text)}</span>` : ""}</span></button>`;
        return advisorySlots
          ? `<div class="rd-fit__choice-item">${button}<div class="rd-fit__choice-advisory" data-rd-fit-inline-advisory="${escapeHtml(value)}" hidden></div></div>`
          : button;
      }).join("");
    }

    syncChoiceButtons(field) {
      const selected = new Set(toArray(this.state[field]));
      this.stage.querySelectorAll(`[data-choice-field="${field}"]`).forEach((choice) => {
        const checked = selected.has(choice.dataset.choiceValue);
        choice.classList.toggle("is-selected", checked);
        choice.setAttribute("aria-checked", String(checked));
        if (choice.dataset.multiple === "false") choice.tabIndex = checked ? 0 : -1;
      });
    }

    updateConditionClearActions() {
      if (!this.stage) return;
      this.stage.querySelectorAll?.("[data-rd-fit-clear-condition]").forEach((button) => {
        const prefix = button.dataset.rdFitClearCondition === "pancreatitis" ? "pancreatitis_" : "gi_";
        const active = toArray(this.state.health).some((value) => String(value).startsWith(prefix));
        button.setAttribute("aria-disabled", String(!active));
        button.classList.toggle("is-disabled", !active);
      });
    }

    clearConditionSelection(kind) {
      const field = kind === "pancreatitis" ? "healthPancreatitisTiming" : kind === "gi" ? "healthGiTiming" : "";
      const prefix = kind === "pancreatitis" ? "pancreatitis_" : kind === "gi" ? "gi_" : "";
      if (!field || !prefix || !toArray(this.state.health).some((value) => String(value).startsWith(prefix))) return;
      this.invalidateAnswerDerivedState();
      this.state[field] = null;
      this.state.health = toArray(this.state.health).filter((value) => !String(value).startsWith(prefix));
      this.saveState();
      this.syncChoiceButtons(field);
      this.syncChoiceButtons("health");
      this.updateConditionClearActions();
      this.syncQuestionState();
    }

    bindQuestionControls() {
      this.stage.querySelectorAll("[data-age-part]").forEach((input) => {
        input.addEventListener("input", () => {
          this.invalidateAnswerDerivedState();
          this.state[input.dataset.agePart] = input.value;
          const age = this.ageInputValue();
          this.state.estimatedAgeMonths = age.valid ? age.months : null;
          this.state.birthday = "";
          this.state.birthMonth = "";
          this.saveState();
          this.syncQuestionState();
        });
      });
      this.stage.querySelectorAll("[data-field]").forEach((input) => {
        input.addEventListener("input", () => {
          this.invalidateAnswerDerivedState();
          this.state[input.dataset.field] = input.value;
          if (this.state.step === 0) this.trackQuizEvent("quiz_started", {}, "quiz_started");
          this.saveState();
          this.syncQuestionState({ drafts: { [input.dataset.field]: input.value } });
        });
      });

      this.stage.querySelectorAll("[data-rd-fit-choice]").forEach((button) => {
        button.addEventListener("click", () => {
          const { choiceField: field, choiceValue: value, multiple } = button.dataset;
          if (field === "birthdayPrecision" && this.state.birthdayPrecision === value) return;
          this.invalidateAnswerDerivedState();
          if (field === "birthdayPrecision") {
            this.state.birthdayPrecision = value;
            this.state.birthday = "";
            this.state.birthMonth = "";
            this.state.estimatedAgeMonths = null;
            this.state.ageYearsInput = "";
            this.state.ageMonthsInput = "";
            this.saveState();
            this.renderQuestion({ focus: false });
            const birthdayFocus = {
              exact_day: "#RDFitBirthday",
              approximate_age: "#RDFitAgeYears",
              month_only: "#RDFitBirthdayMonth",
              unknown: '[data-choice-field="birthdayPrecision"][data-choice-value="unknown"]',
            }[value];
            this.focusAfterRender(birthdayFocus || '[data-choice-field="birthdayPrecision"]');
            return;
          }
          if (multiple === "true") {
            this.state[field] = this.core.normalizeExclusiveSelection(this.state[field], value, "none");
            if (field === "health" && value === "none") {
              this.state.healthPancreatitisTiming = null;
              this.state.healthGiTiming = null;
            }
          } else if (field === "healthPancreatitisTiming" || field === "healthGiTiming") {
            const prefix = field === "healthPancreatitisTiming" ? "pancreatitis_" : "gi_";
            this.state[field] = value;
            this.state.health = toArray(this.state.health).filter((item) => item !== "none" && !String(item).startsWith(prefix));
            this.state.health.push(value);
          } else {
            this.state[field] = value;
            if (field === "primaryOutcome") this.state.outcomes = [value];
            if (field === "bodyCondition") {
              this.state.goal = ({ underweight: "gain", ideal: "maintain", overweight: "lose" })[value];
              this.state.targetWeight = "";
            }
          }
          this.saveState();
          this.syncChoiceButtons(field);
          if (field === "healthPancreatitisTiming" || field === "healthGiTiming") this.syncChoiceButtons("health");
          if (field === "health" && value === "none") {
            this.syncChoiceButtons("healthPancreatitisTiming");
            this.syncChoiceButtons("healthGiTiming");
          }
          this.updateConditionClearActions();
          this.syncQuestionState();
        });
      });

      this.stage.querySelectorAll("[data-rd-fit-clear-condition]").forEach((button) => {
        button.addEventListener("click", () => {
          if (button.getAttribute("aria-disabled") === "true") return;
          this.clearConditionSelection(button.dataset.rdFitClearCondition);
        });
      });

      this.stage.querySelectorAll("[data-rd-fit-choice]").forEach((button) => {
        if (button.dataset.multiple !== "false") return;
        button.addEventListener("keydown", (event) => {
          if (!["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"].includes(event.key)) return;
          const field = button.dataset.choiceField;
          const radios = [...this.stage.querySelectorAll("[data-rd-fit-choice]")].filter((choice) => (
            choice.dataset.choiceField === field && choice.dataset.multiple === "false"
          ));
          if (!radios.length) return;
          event.preventDefault();
          const currentIndex = Math.max(0, radios.indexOf(button));
          const nextIndex = event.key === "Home"
            ? 0
            : event.key === "End"
              ? radios.length - 1
              : (currentIndex + (event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1) + radios.length) % radios.length;
          const next = radios[nextIndex];
          next.click();
          requestAnimationFrame(() => {
            [...this.stage.querySelectorAll("[data-rd-fit-choice]")]
              .find((choice) => choice.dataset.choiceField === field && choice.dataset.choiceValue === next.dataset.choiceValue)
              ?.focus({ preventScroll: true });
          });
        });
      });

      if (this.state.step === 2) this.bindBreedCombobox();
    }

    bindBreedCombobox() {
      const input = this.stage.querySelector("[data-rd-breed-input]");
      const list = this.stage.querySelector("[data-rd-breed-list]");
      const container = this.stage.querySelector("[data-rd-fit-combobox]");
      if (!input || !list || !container) return;

      let matches = [];
      let activeIndex = -1;
      let suppressNextFocusOpen = false;
      const options = () => [...list.querySelectorAll("[data-breed-index]")];
      const closeList = () => {
        list.hidden = true;
        input.setAttribute("aria-expanded", "false");
        input.removeAttribute("aria-activedescendant");
        activeIndex = -1;
      };
      const setActive = (index, { focus = false } = {}) => {
        const choices = options();
        if (!choices.length) return;
        activeIndex = Math.min(choices.length - 1, Math.max(0, Number(index) || 0));
        choices.forEach((choice, choiceIndex) => {
          const active = choiceIndex === activeIndex;
          choice.dataset.active = String(active);
          if (active) input.setAttribute("aria-activedescendant", choice.id);
        });
        const activeOption = choices[activeIndex];
        if (focus) activeOption.focus({ preventScroll: true });
        activeOption.scrollIntoView?.({ block: "nearest" });
      };
      const selectIndex = (index) => {
        const breed = matches[index];
        if (!breed) return false;
        this.invalidateAnswerDerivedState();
        this.state.breed = { name: breed.name };
        input.value = breed.name;
        this.saveState();
        closeList();
        suppressNextFocusOpen = true;
        this.syncQuestionState();
        this.updateBreedNotice();
        input.focus();
        return true;
      };
      const showList = () => {
        const query = input.value.trim().toLowerCase();
        matches = [...this.specialBreeds(), ...this.breeds]
          .filter((breed) => !query || String(breed.name).toLowerCase().includes(query))
          .slice(0, 24);
        activeIndex = matches.findIndex((breed) => breed.name === this.state.breed?.name);
        list.innerHTML = matches.length
          ? matches.map((breed, index) => `<li class="rd-fit__option" id="RDFitBreedOption${index}" role="option" tabindex="-1" aria-selected="${this.state.breed?.name === breed.name}" data-active="false" data-breed-index="${index}"><strong>${escapeHtml(breed.name)}</strong><small>${escapeHtml(breed.size || breed.group || "Reviewed profile")}</small></li>`).join("")
          : '<li class="rd-fit__option rd-fit__option--empty" role="status">No reviewed breed matches. Choose Mixed Breed or Unknown.</li>';
        list.hidden = false;
        input.setAttribute("aria-expanded", "true");
        input.removeAttribute("aria-activedescendant");
        if (activeIndex >= 0) setActive(activeIndex);
        list.querySelectorAll("[data-breed-index]").forEach((option) => {
          option.addEventListener("pointerdown", (event) => {
            event.preventDefault();
            selectIndex(Number(option.dataset.breedIndex));
          });
          option.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              selectIndex(Number(option.dataset.breedIndex));
            } else if (event.key === "Escape") {
              event.preventDefault();
              closeList();
              suppressNextFocusOpen = true;
              input.focus();
            } else if (event.key === "ArrowDown") {
              event.preventDefault();
              setActive(Number(option.dataset.breedIndex) + 1, { focus: true });
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActive(Number(option.dataset.breedIndex) - 1, { focus: true });
            } else if (event.key === "Home") {
              event.preventDefault();
              setActive(0, { focus: true });
            } else if (event.key === "End") {
              event.preventDefault();
              setActive(options().length - 1, { focus: true });
            }
          });
        });
      };

      input.addEventListener("focus", () => {
        if (suppressNextFocusOpen) {
          suppressNextFocusOpen = false;
          return;
        }
        showList();
      });
      input.addEventListener("input", () => {
        this.invalidateAnswerDerivedState();
        this.state.breed = null;
        this.saveState();
        this.syncQuestionState({ drafts: { breedQuery: input.value } });
        this.updateBreedNotice();
        showList();
      });
      input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          closeList();
        } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (list.hidden) showList();
          const nextIndex = event.key === "ArrowDown"
            ? (activeIndex < 0 ? 0 : activeIndex + 1)
            : (activeIndex < 0 ? options().length - 1 : activeIndex - 1);
          // Keep DOM focus in the combobox input and expose the active option
          // through aria-activedescendant. Moving focus into the listbox causes
          // some browsers to emit a focusout with no related target and close it.
          setActive(nextIndex);
        } else if (event.key === "Home" || event.key === "End") {
          if (list.hidden) return;
          event.preventDefault();
          setActive(event.key === "Home" ? 0 : options().length - 1);
        } else if (event.key === "Enter" && !list.hidden) {
          const index = activeIndex < 0 ? 0 : activeIndex;
          if (matches[index]) {
            event.preventDefault();
            selectIndex(index);
          }
        }
      });
      container.addEventListener("focusout", (event) => {
        if (!container.contains(event.relatedTarget)) closeList();
      });
    }

    todayISO() {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 10);
    }

    breedProfile() {
      try {
        return this.core.deriveBreedProfile(
          this.state.breed,
          Number(this.state.currentWeight),
          this.breedTaxonomy,
          this.breeds,
        );
      } catch (_error) {
        return { valid: false, reviewReason: "breed_profile_unavailable" };
      }
    }

    rehydrateStoredBreed() {
      const name = String(this.state?.breed?.name || "").trim();
      if (!name) return null;
      try {
        const profile = this.core.rehydrateBreedSelection(
          { name },
          this.breeds,
          Number(this.state.currentWeight),
          this.breedTaxonomy,
        );
        this.state.breed = { name: String(profile?.name || name) };
        this.saveState();
        return profile;
      } catch (_error) {
        this.state.breed = { name };
        this.saveState();
        return { valid: false, reviewReason: "breed_profile_unavailable" };
      }
    }

    variantsByRole() {
      return this.products.reduce((all, product) => {
        const expectedHandle = product?.role === "variety"
          ? "raw-dawg-mixed-box"
          : this.core.FORMULA_CATALOG?.[product?.role]?.handle;
        if (product?.role) all[product.role] = expectedHandle === product.handle && Array.isArray(product.variants) ? product.variants : [];
        return all;
      }, {});
    }

    recommendationOptions() {
      // These complete ingredient decks belong to the reviewed product handles.
      // A merchant reassigning a role must not silently inherit another formula.
      const formulaCatalog = Object.fromEntries(["beef", "turkey", "salmon"].map((role) => {
        const formula = this.core.FORMULA_CATALOG?.[role];
        const product = this.products.find((item) => item.role === role);
        return [role, formula && product?.handle === formula.handle ? formula : null];
      }));
      return {
        asOf: this.todayISO(),
        taxonomy: this.breedTaxonomy,
        reviewedBreeds: this.breeds,
        variantsByRole: this.variantsByRole(),
        formulaCatalog,
        currency: this.config?.currency || "USD",
      };
    }

    persistAgeMetadata() {
      const asOf = this.todayISO();
      const birthday = this.core.validateBirthday({
        birthday: this.state.birthday,
        birthdayPrecision: this.state.birthdayPrecision,
        estimatedAgeMonths: this.state.estimatedAgeMonths,
      }, { asOf });
      if (!birthday?.valid) return birthday || { valid: false, error: "Add a valid birthday." };
      const age = this.core.deriveAge({ ...this.state, ...birthday }, this.breedProfile(), { asOf });
      if (!age?.valid) return age || { valid: false, error: "We could not calculate the age from that birthday." };
      this.state = {
        ...this.state,
        birthday: birthday.birthday || this.state.birthday || "",
        birthMonth: birthday.birthMonth || this.state.birthMonth || "",
        birthdayPrecision: birthday.birthdayPrecision || this.state.birthdayPrecision,
        estimatedAgeMonths: birthday.estimatedAgeMonths ?? this.state.estimatedAgeMonths ?? null,
        ageAsOf: age.ageAsOf || birthday.ageAsOf || asOf,
        age_as_of: age.ageAsOf || birthday.ageAsOf || asOf,
        nextEligibleDate: age.nextEligibleDate || null,
        next_eligible_date: age.nextEligibleDate || null,
        nextEligibleDatePrecision: age.nextEligibleDatePrecision || null,
      };
      return { valid: true, birthday, age };
    }

    validateQuestion() {
      const evaluation = this.syncQuestionState();
      if (!evaluation.complete) {
        const target = evaluation.target ? this.stage.querySelector(evaluation.target) : null;
        const error = this.stage.querySelector("[data-rd-fit-error]");
        if (error) {
          error.textContent = evaluation.message || "Complete this answer before continuing.";
          error.hidden = false;
        }
        if (target) {
          const currentDescription = String(target.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
          if (!currentDescription.includes("RDFitQuestionError")) currentDescription.push("RDFitQuestionError");
          target.setAttribute("aria-describedby", currentDescription.join(" "));
          target.focus?.({ preventScroll: true });
        }
        return false;
      }
      if (this.state.step === 1) {
        const age = this.persistAgeMetadata();
        if (!age.valid) return false;
      }
      if (this.state.step === 3) this.state.bodyConditionReconfirmationPending = false;
      this.saveState();
      return true;
    }

    goNext() {
      if (this.state.view === "v34_intro") return this.start();
      if (this.state.view === "v34_education") {
        this.state.v34EducationSeen = { ...(this.state.v34EducationSeen || {}), [this.state.step]: true };
        if (this.state.step === TOTAL_STEPS - 1) return this.complete();
        this.state.step += 1;
        this.state.view = this.state.currentView = "question";
        this.saveState();
        this.pushHistory();
        return this.renderCurrent();
      }
      if (this.state.view === "email") return this.skipEmail();
      if (!this.validateQuestion()) return;
      if (this.state.step === 0) this.trackQuizEvent("quiz_started", {}, "quiz_started");
      this.trackQuizEvent("quiz_step_completed", {
        question_number: this.state.step + 1,
        question_id: QUESTION_IDS[this.state.step],
      }, `quiz_step_completed_${this.state.step + 1}`);
      if (this.state.returnToEmailAfterEdit === true) {
        delete this.state.returnToEmailAfterEdit;
        delete this.state.emailEditStep;
        this.invalidateAnswerDerivedState();
        this.complete({ returnToEmail: true });
        return;
      }
      if (this.state.returnToResultAfterEdit === true) {
        delete this.state.returnToResultAfterEdit;
        delete this.state.resultEditStep;
        this.complete({ returnToResult: true });
        return;
      }
      if (EDUCATION_SCREENS[this.state.step] && !this.state.v34EducationSeen?.[this.state.step]) {
        this.state.view = this.state.currentView = "v34_education";
        this.saveState();
        this.pushHistory();
        this.renderCurrent();
        return;
      }
      if (this.state.step === TOTAL_STEPS - 1) {
        this.complete();
        return;
      }
      this.state.step += 1;
      this.state.view = "question";
      this.state.currentView = "question";
      this.saveState();
      this.pushHistory();
      this.renderCurrent();
    }

    goBack() {
      if (this.state.view === "question" && (this.state.returnToEmailAfterEdit || this.state.returnToResultAfterEdit) && !this.validateQuestion()) return;
      if (this.state.view === "question" && this.state.returnToEmailAfterEdit === true) {
        delete this.state.returnToEmailAfterEdit;
        delete this.state.emailEditStep;
        this.state.view = "email";
        this.state.currentView = "email";
      } else if (this.state.view === "question" && this.state.returnToResultAfterEdit === true) {
        delete this.state.returnToResultAfterEdit;
        delete this.state.resultEditStep;
        this.state.view = "result";
        this.state.currentView = "result";
      } else if (this.state.view === "email") {
        this.state.view = "v34_education";
        this.state.currentView = "v34_education";
        this.state.step = TOTAL_STEPS - 1;
      } else if (["interstitial", "chapter", "v34_education"].includes(this.state.view)) {
        this.state.view = "question";
        this.state.currentView = "question";
      } else if (this.state.view === "result") {
        this.state.view = "question";
        this.state.currentView = "question";
        this.state.step = TOTAL_STEPS - 1;
      } else if (this.state.step > 0) {
        this.state.step -= 1;
        if (EDUCATION_SCREENS[this.state.step] && this.state.v34EducationSeen?.[this.state.step]) this.state.view = this.state.currentView = "v34_education";
      } else {
        this.state.view = "v34_intro";
        this.state.currentView = "v34_intro";
      }
      this.saveState();
      this.pushHistory(true);
      this.renderCurrent();
    }

    complete({ returnToEmail = false, returnToResult = false } = {}) {
      if (this.enforceBodyConditionReconfirmation(true)) {
        this.renderCurrent();
        return;
      }
      this.persistAgeMetadata();
      this.ensureCompletionId();
      if (!this.state.completionSnapshotAt) this.state.completionSnapshotAt = new Date().toISOString();
      const result = this.core.calculateResult(this.state, this.recommendationOptions());
      this.state.result = this.evaluateCartReadiness(result);
      this.state.view = returnToResult ? "result" : "email";
      this.state.currentView = this.state.view;
      this.trackQuizEvent("quiz_completed", {
        result_status: this.state.result?.status || "unknown",
        recommendation: this.resultRole(this.state.result) || "none",
        product_key: this.resultRole(this.state.result) || "none",
        purchase_allowed: this.state.result?.purchaseAllowed === true,
      }, "quiz_completed");
      if (!returnToResult) {
        this.showEmailStep();
        return;
      }
      this.saveState();
      this.pushHistory();
      this.renderCurrent();
    }

    resultRole(result) {
      return result?.productRole || result?.role || result?.futureProductRole || result?.future_product_role || null;
    }

    getProduct(result) {
      const role = this.resultRole(result);
      return role ? this.products.find((product) => product.role === role) || null : null;
    }

    rawFitGuideUrl() {
      const configured = String(this.config?.rawFitGuideUrl || this.config?.guideUrl || "").trim();
      return /^(?:\/|https?:\/\/)/i.test(configured) ? configured : "";
    }

    formatBoxOrderItem(quantity, size) {
      const count = Number(quantity);
      const poundsPerBox = Number(size);
      if (!Number.isInteger(count) || count <= 0 || !Number.isFinite(poundsPerBox) || poundsPerBox <= 0) return "";
      return `${count} × ${numberText(poundsPerBox)} lb box${count === 1 ? "" : "es"}`;
    }

    boxPlanMessage(boxPlan) {
      if (!boxPlan || typeof boxPlan !== "object") return "";
      const cadenceWeeks = this.supportedCadenceWeeks(boxPlan.cadenceWeeks);
      const required = Number(cadenceWeeks
        ? (boxPlan.requiredPounds28Days ?? boxPlan.requiredPounds)
        : (boxPlan.requiredPounds ?? boxPlan.requiredPounds28Days ?? boxPlan.fourWeekPounds));
      const delivered = Number(cadenceWeeks
        ? (boxPlan.deliveredPounds28Days ?? boxPlan.total ?? boxPlan.deliveredPoundsPerShipment)
        : (boxPlan.total ?? boxPlan.deliveredPounds28Days ?? boxPlan.deliveredPoundsPerShipment));
      const difference = Number(boxPlan.differencePounds28Days ?? (Number.isFinite(delivered) && Number.isFinite(required) ? delivered - required : NaN));
      const shortfall = Number((cadenceWeeks ? boxPlan.shortfallPounds28Days : boxPlan.shortfallPounds) ?? (Number.isFinite(difference) ? Math.max(0, -difference) : NaN));
      const overage = Number((cadenceWeeks ? boxPlan.overagePounds28Days : boxPlan.overagePounds) ?? (Number.isFinite(difference) ? Math.max(0, difference) : NaN));
      const tolerance = 0.000001;
      if (Number.isFinite(shortfall) && shortfall > tolerance) {
        const days = Number(cadenceWeeks ? boxPlan.daysNotCovered28Days : boxPlan.daysNotCovered);
        const dayCopy = Number.isInteger(days) && days > 0
          ? (days === 1 ? "About 1 day is not covered." : `About ${days} days are not covered.`)
          : "";
        return `This lower-box recommendation is ${numberText(shortfall)} lb short of the 28-day estimate.${dayCopy ? ` ${dayCopy}` : ""}`;
      }
      if (Number.isFinite(overage) && overage > tolerance) {
        return `${numberText(overage)} lb beyond the 28-day estimate. This recommendation prioritizes fewer boxes, then the closest covering amount.`;
      }
      if (Number.isFinite(required) && Number.isFinite(delivered) && Math.abs(delivered - required) <= tolerance) {
        return "Exactly covers the 28-day estimate.";
      }
      return "";
    }

    expectedLineCount(result) {
      return Object.values(result?.boxPlan?.quantities || {}).filter((quantity) => Number(quantity) > 0).length;
    }

    variantForSize(product, size, role, result = {}) {
      const selector = this.core.selectRecommendedSubscriptionVariant || this.core.selectRecommendedVariant;
      if (typeof selector !== "function") return null;
      const expected = toArray(result.variantSelections)
        .find((selection) => Number(selection?.size) === Number(size));
      const candidates = expected
        ? (product.variants || []).filter((variant) => String(variant?.id) === String(expected.variantId))
        : (product.variants || []);
      if (expected && candidates.length !== 1) return null;
      const selection = selector.call(this.core, role, size, candidates, this.effectiveAvoidances(result), Number(result.purchaseCadenceWeeks) || 4);
      if (!selection?.valid || !selection.variant) return null;
      if (expected && String(selection.variant.id) !== String(expected.variantId)) return null;
      const sellingPlanId = result?.purchaseType === "subscription"
        ? String(
          expected?.sellingPlanId
          || result?.sellingPlan?.linePlanIds?.[String(size)]
          || result?.sellingPlan?.id
          || "",
        ) || null
        : null;
      const availablePlans = selection.sellingPlans || selection.variant.sellingPlans || [];
      const selectedPlan = sellingPlanId
        ? availablePlans.find((plan) => String(plan?.id) === sellingPlanId)
        : (result?.purchaseType === "subscription" && typeof this.core.fourWeekPlansForVariant === "function"
          ? this.core.fourWeekPlansForVariant(selection.variant)[0] || null
          : null);
      if (result?.purchaseType === "subscription" && result?.subscriptionPlanStatus === "verified" && !selectedPlan) return null;
      return { ...selection.variant, proteinComposition: selection.proteinComposition, sellingPlans: selection.sellingPlans || selection.variant.sellingPlans || [], sellingPlanId: selectedPlan?.id || null };
    }

    cartLines(result, product = this.getProduct(result)) {
      if (!result?.purchaseAllowed || !product || !this.resultRole(result)) return [];
      return Object.entries(result.boxPlan?.quantities || {})
        .filter(([, quantity]) => Number(quantity) > 0)
        .map(([size, quantity]) => {
          const variant = this.variantForSize(product, Number(size), this.resultRole(result), result);
          return {
            size: Number(size),
            quantity: Number(quantity),
            variant,
            sellingPlanId: result.purchaseType === "subscription" ? (variant?.sellingPlanId || null) : null,
          };
        });
    }

    validateCartLines(lines, result) {
      if (!Array.isArray(lines) || !lines.length || lines.some((line) => !line.variant)) {
        return { valid: false, reason: "A compatible product variant could not be verified." };
      }
      try {
        return this.core.validateCartLines(lines, this.state.proteins || [], result);
      } catch (_error) {
        return { valid: false, reason: "A compatible product variant could not be verified." };
      }
    }

    holdForUnverifiedProduct(result, reason) {
      const retainedStatus = ["wait", "conditional", "choice_required"].includes(result?.status)
        ? result.status
        : "no_sale";
      return {
        ...result,
        status: retainedStatus,
        route: "no_compatible_product",
        reason: reason || "We could not verify a compatible Raw Dawg box for the answers provided, so we will not recommend one.",
        productRole: null,
        role: null,
        purchaseAllowed: false,
        boxPlan: null,
        cartLines: [],
        cartValidation: { valid: false, reason: reason || "No verified compatible product variant." },
      };
    }

    evaluateCartReadiness(result) {
      const canValidateFuturePlan = ["wait", "conditional"].includes(result?.status);
      if ((!result?.purchaseAllowed && !canValidateFuturePlan) || !this.resultRole(result)) return { ...result, cartLines: [], futureCartLines: [] };
      const product = this.getProduct(result);
      if (!product) return this.holdForUnverifiedProduct(result);
      const lines = Array.isArray(result.cartLines) && result.cartLines.length
        ? result.cartLines
        : this.cartLines({ ...result, purchaseAllowed: true }, product);
      if (lines.length !== this.expectedLineCount(result)) return this.holdForUnverifiedProduct(result);
      const validationTarget = canValidateFuturePlan ? { ...result, purchaseAllowed: true } : result;
      const validation = this.validateCartLines(lines, validationTarget);
      if (!validation?.valid) return this.holdForUnverifiedProduct(result, validation?.reason);
      return result.purchaseAllowed === true
        ? { ...result, cartLines: lines, cartValidation: validation }
        : { ...result, cartLines: [], futureCartLines: lines, futureCartValidation: validation };
    }

    showEmailStep() {
      this.state.view = "email";
      this.state.currentView = "email";
      this.saveState();
      this.pushHistory();
      this.renderCurrent();
    }

    emailSummaryRows() {
      const label = (value) => String(value || "")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
      const breed = this.state.breed?.name || "Not selected";
      const birthday = this.state.birthdayPrecision === "exact_day"
        ? this.state.birthday || "Not selected"
        : this.state.birthdayPrecision === "approximate_age"
          ? (this.state.estimatedAgeMonths != null && String(this.state.estimatedAgeMonths).trim() !== "" && Number.isFinite(Number(this.state.estimatedAgeMonths))
            ? `${Math.floor(Number(this.state.estimatedAgeMonths) / 12)} years, ${Number(this.state.estimatedAgeMonths) % 12} months (estimated)`
            : "Not provided")
        : this.state.birthdayPrecision === "month_only"
          ? `${this.state.birthday || "Not selected"} (approximate)`
          : "Not sure yet";
      const proteinAvoidances = toArray(this.state.proteins).filter((value) => value !== "none");
      const avoided = proteinAvoidances.length ? proteinAvoidances.join(", ") : "None";
      const conditions = toArray(this.state.health).filter((value) => value !== "none");
      const bodyCondition = label(this.state.bodyCondition || "ideal");
      return [
        { label: "Name", value: this.state.name || "Your dawg", step: 0 },
        { label: "Age / birthday", value: birthday, step: 1 },
        { label: "Breed", value: breed, step: 2 },
        { label: "Weight / body condition", value: `${pounds(this.state.currentWeight)} · ${bodyCondition}`, step: 3 },
        { label: "Activity", value: label(this.state.activity || "Not selected"), step: 4 },
        { label: "Raw experience", value: label(this.state.rawStatus || "Not selected"), step: 5 },
        { label: "Protein avoidances", value: avoided, step: 6 },
        { label: "Safety check", value: conditions.length ? conditions.map(label).join(", ") : "None selected", step: 7 },
        { label: "Primary outcome", value: label(this.state.primaryOutcome || toArray(this.state.outcomes)[0] || "No single priority"), step: 8 },
      ];
    }

    editEmailSection(step) {
      const nextStep = Math.max(0, Math.min(TOTAL_STEPS - 1, Number(step)));
      this.trackQuizEvent("quiz_answer_edit", {
        question_number: nextStep + 1,
        question_id: QUESTION_IDS[nextStep],
        action: "edit_from_summary",
      });
      this.state.view = "question";
      this.state.currentView = "question";
      this.state.step = nextStep;
      this.state.returnToEmailAfterEdit = true;
      this.state.emailEditStep = nextStep;
      this.saveState();
      this.pushHistory(true);
      this.renderCurrent();
    }

    editResultSection(step) {
      const nextStep = Math.max(0, Math.min(TOTAL_STEPS - 1, Number(step)));
      this.trackQuizEvent("quiz_answer_edit", {
        question_number: nextStep + 1,
        question_id: QUESTION_IDS[nextStep],
        action: "edit_from_result",
      });
      this.state.view = "question";
      this.state.currentView = "question";
      this.state.step = nextStep;
      this.state.returnToResultAfterEdit = true;
      this.state.resultEditStep = nextStep;
      this.saveState();
      this.pushHistory(true);
      this.renderCurrent();
    }

    renderEmailStep({ focus = true } = {}) {
      if (this.enforceBodyConditionReconfirmation(true)) {
        this.renderCurrent({ focus });
        return;
      }
      if (!this.state.email) {
        const customerEmail = window.Shopify?.customer?.email || window.Shopify?.customer?.default_address?.email;
        if (typeof customerEmail === "string" && /^\S+@\S+\.\S+$/.test(customerEmail)) {
          this.state.email = customerEmail;
          this.saveState();
        }
      }
      this.trackQuizEvent("quiz_email_view", { email_step: "view" }, "quiz_email_view");
      this.footer.hidden = false;
      this.progressWrap.hidden = true;
      this.backButton.hidden = false;
      this.nextButton.hidden = true;
      // Answer edits invalidate the cached result. Back/history may return here
      // without complete(), so the email view must rebuild from current answers.
      this.persistAgeMetadata();
      const result = this.evaluateCartReadiness(this.core.calculateResult(this.state, this.recommendationOptions()));
      this.state.result = result;
      this.saveState();
      const status = result.status || "no_sale";
      const tip = status === "wait" || status === "conditional"
        ? "We will keep the future route clear, including what needs to change before a bowl is the right next step."
        : "Your plan includes a starting amount, transition notes, and a few Bowl Remix ideas.";
      const emailNote = this.config.result?.emailNote || "Your $10 offer code will be emailed with your plan, feeding guide, and Bowl Remix ideas when you choose Send My Results.";
      const marketingLabel = this.config.result?.marketingLabel || "Send my results, the $10 offer, and ongoing Raw Dawg emails.";
      const rows = this.emailSummaryRows().map((row) => `<li><span>${escapeHtml(row.label)}</span><strong>${escapeHtml(row.value)}</strong><button type="button" class="rd-fit__edit-link" data-rd-fit-edit-section="${row.step}">Edit</button></li>`).join("");
      const summaryOpen = window.matchMedia?.("(min-width: 750px)")?.matches ? " open" : "";
      this.stage.innerHTML = `
        <div class="rd-fit__view rd-fit__view--email" data-rd-fit-email-step>
          <p class="rd-fit__eyebrow">Your Raw Fit plan</p>
          ${this.viewTitleMarkup("RDFitEmailTitle", "rd-fit__heading", this.replaceTokens("Where should we send [dog_name]’s plan?"))}
          <p class="rd-fit__lede rd-fit__email-lede"><strong>${this.replaceTokens(emailNote)}</strong> ${escapeHtml(tip)}</p>
          <div class="rd-fit__field-row">
            <div class="rd-fit__field"><label for="RDFitFirstName">Your first name <span class="rd-fit__optional">(optional)</span></label><input class="rd-fit__input" id="RDFitFirstName" type="text" autocomplete="given-name" data-rd-fit-first-name value="${escapeHtml(this.state.firstName || "")}"></div>
            <div class="rd-fit__field"><label for="RDFitEmail">Email address</label><input class="rd-fit__input" id="RDFitEmail" type="email" required autocomplete="email" data-rd-fit-email value="${escapeHtml(this.state.email || "")}" placeholder="you@example.com"></div>
          </div>
          <p class="rd-fit__consent-copy">By selecting <strong>Send My Results</strong>, you agree to: ${this.replaceTokens(marketingLabel)} You can unsubscribe anytime.</p>
          <div class="rd-fit__email-actions">
            <button class="button button--primary" type="button" data-rd-fit-send-results><span class="button__content"><span class="button__label">Send My Results</span></span><span class="animated-button-circle"></span></button>
            <button class="rd-fit__skip-email" type="button" data-rd-fit-skip-email>Skip — show my results</button>
          </div>
          <p class="rd-fit__cart-message" id="RDFitEmailMessage" role="status" data-rd-fit-email-message hidden></p>
          <details class="rd-fit__email-summary" aria-label="Your quiz answers"${summaryOpen}>
            <summary class="rd-fit__email-summary-head"><span class="rd-fit__summary-title">Review your answers</span><span>Questions 1–9</span></summary>
            <ul>${rows}</ul>
          </details>
          <div class="rd-fit__email-tips"><h3>A head start for the bowl</h3><ul><li>Keep the first transition gradual.</li><li>Use the calculated amount as a starting point and watch body condition.</li><li>${SAFE_HANDLING}</li></ul></div>
        </div>`;
      this.stage.querySelectorAll("[data-rd-fit-edit-section]").forEach((button) => button.addEventListener("click", () => this.editEmailSection(button.dataset.rdFitEditSection)));
      const emailInput = this.stage.querySelector("[data-rd-fit-email]");
      const emailMessage = this.stage.querySelector("[data-rd-fit-email-message]");
      this.stage.querySelector("[data-rd-fit-first-name]")?.addEventListener("input", (event) => { this.state.firstName = event.target.value; this.saveState(); });
      emailInput?.addEventListener("input", (event) => {
        this.state.email = event.target.value;
        this.clearFieldError(emailInput, emailMessage);
        this.saveState();
      });
      this.stage.querySelector("[data-rd-fit-skip-email]")?.addEventListener("click", () => this.skipEmail());
      this.stage.querySelector("[data-rd-fit-send-results]")?.addEventListener("click", () => this.saveEmailPlan(result));
      if (focus) this.focusAfterRender("[data-rd-fit-view-title]");
    }

    skipEmail() {
      this.trackQuizEvent("quiz_email_skip", { email_step: "skip" }, "quiz_email_skip");
      this.state.view = "result";
      this.state.currentView = "result";
      this.saveState();
      this.pushHistory();
      this.renderCurrent();
    }

    resultHeading(result) {
      const dog = String(this.state.name || "Your dawg").trim() || "Your dawg";
      if (result.status === "wait") return this.tokenText(this.config.result?.waitHeading || "[dog_name] should wait for now.");
      if (result.status === "conditional") return this.tokenText(this.config.result?.conditionalHeading || "Confirm [dog_name]’s route before feeding.");
      if (result.status === "choice_required") return "A few blends fit the brief.";
      if (result.status === "no_sale") return "We do not have a compatible box match yet.";
      return this.tokenText(this.config.result?.eligibleHeading || "[dog_name]’s starting plan.");
    }

    resultStatus(result) {
      if (result.status === "wait") return "Do not feed yet";
      if (result.status === "conditional") return "Confirm before feeding";
      if (result.status === "choice_required") return "Choose from compatible blends";
      if (result.status === "no_sale") return "No available box match";
      return "Your starting plan";
    }

    selectedMix(line) {
      return (line?.variant?.options || []).find((option) => !/^\d+\s*lb$/i.test(String(option).trim())) || line?.variant?.title || "";
    }

    productDisplayName(product, lines) {
      const mix = this.selectedMix(lines?.[0]);
      return [product?.title, mix].filter(Boolean).join(" · ");
    }

    resultLead(result) {
      const dog = this.state.name || "your dawg";
      if (result.status === "wait") return `Here is ${dog}'s future blend and bowl plan. Do not feed it until the hold below has cleared.`;
      if (result.status === "conditional") return `Here is ${dog}'s future blend and bowl plan. Confirm the route before feeding.`;
      if (result.status === "choice_required") return `More than one formula fits the ingredient screen. Review the diet watch, then choose the blend you want to use.`;
      if (result.status === "no_sale") return "We could not find an available box that meets your answers and our breed rules. Review your answers or contact Raw Dawg for help.";
      return `An estimated starting amount and product match for ${dog}, based on the answers you provided.`;
    }

    beforeFeedingInstruction(result) {
      const holds = new Set(toArray(result?.holds));
      const dog = this.state.name || "your dawg";
      if (holds.has("puppy")) {
        const eligible = result?.age?.nextEligibleDate || this.state.nextEligibleDate;
        return `Wait until ${dog} reaches the adult eligibility ${eligible ? `date (${eligible})` : "window"}, then recheck the plan before feeding.`;
      }
      if (holds.has("antibiotics_current") || holds.has("antibiotics_recent")) return "Wait while antibiotics are being taken and until at least 14 days after the treatment round ends, then confirm the next step.";
      if (holds.has("pregnant") || holds.has("nursing")) return "Do not start this raw plan during pregnancy or nursing. Recheck the route after that stage has ended.";
      if (holds.has("immune_immunosuppression")) return "Do not start this raw plan while the immune condition or immunosuppressive treatment is current. Confirm the future route with the treating professional.";
      if (holds.has("pancreatitis_active_recovering") || holds.has("gi_active_recovering")) return "Wait until the condition is no longer active or recovering, then confirm the future formula and transition with the treating professional.";
      if (result.status === "conditional") return "Because the condition history is stable rather than active, confirm the formula and transition with the treating professional before feeding.";
      return "Confirm that the waiting condition has cleared, then return to this plan before feeding.";
    }

    guardFingerprint(result) {
      const source = result?.guardFingerprintSource || result;
      const lines = (source?.cartLines?.length ? source.cartLines : (source?.futureCartLines || []))
        .map((line) => ({
          quantity: Number(line?.quantity || 0),
          sellingPlanId: String(line?.sellingPlanId || source?.sellingPlan?.id || ""),
          size: Number(line?.size || 0),
          variantId: String(line?.variant?.id || line?.variantId || ""),
        }))
        .sort((left, right) => `${left.size}:${left.variantId}`.localeCompare(`${right.size}:${right.variantId}`));
      return JSON.stringify({
        status: String(source?.status || ""),
        role: String(this.resultRole(source) || ""),
        holds: toArray(source?.holds).map(String).sort(),
        quantities: source?.boxPlan?.quantities || {},
        lines,
      });
    }

    guardIsCurrent(result) {
      return ["wait", "conditional"].includes(result?.status)
        && this.state.guardAcknowledged === true
        && this.state.guardAcknowledgementFingerprint === this.guardFingerprint(result);
    }

    renderResult({ focus = true } = {}) {
      if (this.enforceBodyConditionReconfirmation(true)) {
        this.renderCurrent({ focus });
        return;
      }
      const raw = this.core.calculateResult(this.state, this.recommendationOptions());
      const result = this.evaluateCartReadiness(raw);
      this.state.result = result;
      this.footer.hidden = true;
      this.progressWrap.hidden = true;
      const ready = result.status === "ready" || result.status === "eligible";
      const guarded = ["wait", "conditional"].includes(result.status);
      const choiceSelected = result.status === "choice_required" && result.selectionRequired === false;
      const futureRole = result.futureProductRole || result.future_product_role || result.productRole || result.role;
      const product = futureRole ? this.products.find((item) => item.role === futureRole) || null : null;
      this.trackQuizEvent("quiz_result_view", {
        result_status: result.status || "unknown",
        product_key: futureRole || "none",
      }, "quiz_result_view");
      const purchaseAllowed = (ready || choiceSelected) && result.purchaseAllowed === true && result.cartValidation?.valid === true;
      const lines = result.cartLines?.length ? result.cartLines : (result.futureCartLines || []);
      const purchaseSelection = this.resolvePurchaseSelection(result);
      const baselineSelection = purchaseSelection.selections.find((selection) => selection.weeks === 4) || null;
      const selectedShipment = purchaseSelection.purchaseType === "subscription"
        ? purchaseSelection.selectedCadence
        : baselineSelection;
      const displayCadenceWeeks = selectedShipment?.weeks || 4;
      const displayPlan = selectedShipment?.boxPlan || result.boxPlan;
      const displayLines = selectedShipment?.lines || lines;
      const displayPricing = selectedShipment?.pricing || result.pricing;
      const productTitle = product ? this.productDisplayName(product, displayLines) : "";
      const hasMetrics = result.dailyOz !== null && result.dailyOz !== undefined && Number.isFinite(Number(result.dailyOz));
      const meals = [1, 2, 3].includes(Number(this.state.mealsPerDay)) ? Number(this.state.mealsPerDay) : 2;
      this.state.mealsPerDay = meals;
      const perMeal = hasMetrics ? Number(result.dailyOz) / meals : null;
      const coverageDays = displayCadenceWeeks === 2
        ? displayPlan?.coverageDaysPerShipment
        : (displayPlan?.coverageDays ?? result.coverageDays);
      const requiredPounds28Days = Number(displayPlan?.requiredPounds28Days ?? displayPlan?.requiredPounds ?? result.fourWeekPounds ?? result.requiredPounds);
      const deliveredPoundsForEstimate = Number(displayPlan?.deliveredPounds28Days ?? displayPlan?.total ?? result.totalOrderedPounds);
      const shortfallPounds = Number(displayPlan?.shortfallPounds28Days ?? displayPlan?.shortfallPounds ?? (
        Number.isFinite(requiredPounds28Days) && Number.isFinite(deliveredPoundsForEstimate)
          ? Math.max(0, requiredPounds28Days - deliveredPoundsForEstimate)
          : 0
      ));
      const boxRows = displayLines.map((line) => `<li><span>${escapeHtml(this.formatBoxOrderItem(line.quantity, line.size))}</span><strong>${escapeHtml(pounds(line.quantity * line.size))}</strong></li>`).join("");
      const boxPlanMessage = this.boxPlanMessage(displayPlan);
      const perShipmentPounds = displayCadenceWeeks === 2
        ? (displayPlan?.deliveredPoundsPerShipment ?? displayPlan?.total)
        : (displayPlan?.total ?? result.totalOrderedPounds);
      const deliveredPounds28Days = displayCadenceWeeks === 2
        ? (displayPlan?.deliveredPounds28Days ?? Number(perShipmentPounds) * 2)
        : perShipmentPounds;
      const coverageMetric = Number.isFinite(Number(coverageDays)) ? numberText(coverageDays) : "—";
      const boxPlanNote = shortfallPounds > 0.000001 ? "" : boxPlanMessage;
      const rawFitGuideUrl = this.rawFitGuideUrl();
      const reasons = []; // Machine reason codes stay in the plan data, not customer copy.
      const reasonsMarkup = reasons.length ? `<section class="rd-fit__reasons" aria-labelledby="RDFitReasonsTitle"><h3 id="RDFitReasonsTitle">Why this route</h3><ul class="rd-fit__reason-list">${reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join("")}</ul></section>` : "";
      const currentGuardAcknowledged = this.guardIsCurrent(result);
      const planRequestAcknowledgement = this.state.planRequestAcknowledged === true
        ? `<p class="rd-fit__cart-message" role="status" data-rd-fit-plan-request-acknowledgement>Your plan request was accepted. Watch your inbox for the plan and $10 offer.</p>`
        : "";
      const holdInstructionMarkup = guarded ? `<p class="rd-fit__hold-instruction"><strong>Before feeding:</strong> ${escapeHtml(this.beforeFeedingInstruction(result))}</p>` : "";
      const guardedMarkup = "";
      const choiceRoles = new Set(result.compatibleRoles || this.products.map((item) => item.role));
      const showChoiceGrid = result.status === "choice_required" || result.selectionRequired === true;
      const choiceGrid = showChoiceGrid ? `<section class="rd-fit__choice-grid" aria-labelledby="RDFitChoiceTitle"><h3 id="RDFitChoiceTitle">Choose a compatible blend</h3><p>${escapeHtml(result.reason || "These options remain compatible with the answers provided.")}</p>${this.products.filter((item) => choiceRoles.has(item.role)).map((item) => {
        const selected = item.role === futureRole;
        return `<article class="rd-fit__choice-product${selected ? " is-selected" : ""}"><h4>${escapeHtml(item.title || item.role)}</h4><button class="rd-fit__secondary-action" type="button" data-rd-fit-choice-product="${escapeHtml(item.role)}" aria-pressed="${selected}">${selected ? "Selected" : "Choose this blend"}</button></article>`;
      }).join("")}</section>` : "";
      const alternativeRole = result.secondaryProductRole || result.secondary_product_role || result.alternativeRole;
      const alternative = alternativeRole ? this.products.find((item) => item.role === alternativeRole) : null;
      const resultAnswerRows = this.emailSummaryRows().map((row) => `<li><span>${escapeHtml(row.label)}</span><strong>${escapeHtml(row.value)}</strong><button type="button" class="rd-fit__edit-link" data-rd-fit-edit-result="${row.step}">Edit</button></li>`).join("");
      this.stage.innerHTML = `
        <div class="rd-fit__view rd-fit__view--result">
          <p class="rd-fit__eyebrow">${this.replaceTokens(this.config.result?.eyebrow || "[dog_name]'S RAW FIT PLAN")}</p>
          ${this.viewTitleMarkup("RDFitResultTitle", "rd-fit__heading", escapeHtml(this.resultHeading(result)))}
          <div class="rd-fit__result-summary">
            ${this.breedNoticeMarkup(result.breedNotices || [])}
            <section class="rd-fit__result-hero" aria-labelledby="RDFitStatusTitle">
              <span class="rd-fit__result-status" id="RDFitStatusTitle">${escapeHtml(this.resultStatus(result))}</span>
              <p class="rd-fit__lede">${escapeHtml(this.resultLead(result))}</p>
              ${result.status === "no_sale" ? `<p><a href="/pages/contact">Contact Raw Dawg about your dog’s needs</a></p>` : ""}
              ${planRequestAcknowledgement}
              ${holdInstructionMarkup}
            </section>
            <div class="rd-fit__decision-grid">
              <div class="rd-fit__plan-stack">
                ${hasMetrics ? `<section class="rd-fit__feeding-card" aria-labelledby="RDFitFeedingTitle"><div class="rd-fit__section-heading"><h3 id="RDFitFeedingTitle">Feeding plan</h3><span>${displayCadenceWeeks === 2 ? "Ships every 2 weeks" : "28-day starting estimate"}</span></div><div class="rd-fit__portion-metrics"><div class="rd-fit__portion-metric"><strong>${escapeHtml(numberText(result.dailyOz))}</strong><span>oz per day</span></div><div class="rd-fit__portion-metric"><strong>${escapeHtml(numberText(perMeal))}</strong><span>oz per meal · <select class="rd-fit__inline-select" aria-label="Meals per day" data-rd-fit-meals>${[1, 2, 3].map((value) => `<option value="${value}" ${value === meals ? "selected" : ""}>${value}</option>`).join("")}</select> meals</span></div></div></section>` : ""}
                ${this.transitionMarkup()}
              ${product ? `<section class="rd-fit__recommendation" aria-labelledby="RDFitBlendTitle">${product.featuredImage ? `<img src="${escapeHtml(product.featuredImage)}" alt="" width="82" height="100">` : ""}<div><p class="rd-fit__eyebrow">${result.status === "choice_required" ? "Selected compatible blend" : guarded ? "Future recommendation" : "Recommended blend"}</p><h3 id="RDFitBlendTitle">${escapeHtml(productTitle || product.title || futureRole)}</h3><p>${this.replaceTokens("Selected using our breed rules and the protein avoidances you listed. Review the full ingredients against [dog_name]’s needs.")}</p></div></section>` : ""}
              ${choiceGrid}
                ${hasMetrics ? `<div class="rd-fit__box-plan"><h4>28-day box plan</h4><div class="rd-fit__box-metrics"><div><strong>${escapeHtml(numberText(result.fourWeekPounds || result.requiredPounds))}</strong><span>lb needed</span></div><div><strong>${escapeHtml(numberText(deliveredPounds28Days))}</strong><span>lb delivered</span></div><div><strong>${escapeHtml(coverageMetric)}</strong><span>${displayCadenceWeeks === 2 ? "days per shipment" : "days covered"}</span></div></div>${displayLines.length ? `<ul class="rd-fit__plan-list">${boxRows}</ul>` : ""}${boxPlanNote ? `<p class="rd-fit__box-plan-note">${escapeHtml(boxPlanNote)}</p>` : ""}</div>` : ""}
                ${guardedMarkup}
                ${purchaseAllowed ? this.builderMarkup({ ...result, displayBoxPlan: displayPlan, displayPricing, purchaseAllowed, guardAcknowledged: true, cartValidation: result.cartValidation, cartLines: lines }) : ""}
              </div>
            </div>
            ${reasonsMarkup}
            ${alternative ? `<section class="rd-fit__alternative"><p class="rd-fit__eyebrow">Also compatible</p><h3>${escapeHtml(alternative.title || alternative.role)}</h3><p>This option remains in the compatible set for the answers you provided. Review its ingredients and plan details before selecting it.</p></section>` : ""}
            <section class="rd-fit__tips"><h3>Make the switch stick</h3><ul><li>Transition gradually and watch body condition and routine.</li><li>${SAFE_HANDLING}</li><li>During transition, serve the same measured portion without adding extras. Save Bowl Remixes for after the switch.</li></ul>${rawFitGuideUrl ? `<a href="${escapeHtml(rawFitGuideUrl)}" target="_blank" rel="noopener">Read the Raw Fit guide</a>` : ""}</section>
            <details class="rd-fit__answer-review"><summary>Review or edit ${escapeHtml(this.state.name || "your dawg")}'s answers</summary><ul>${resultAnswerRows}</ul></details>
            <section class="rd-fit__phone-box" aria-labelledby="RDFitPhoneTitle"><h3 id="RDFitPhoneTitle">Keep me in the pack by text.</h3><p class="rd-fit__inline-note">Optional. We’ll only use this number for the SMS updates you explicitly choose; detailed quiz results stay out of text messages.</p><div class="rd-fit__email-row"><label class="visually-hidden" for="RDFitPhone">Phone number</label><input class="rd-fit__input" id="RDFitPhone" type="tel" inputmode="tel" autocomplete="tel" placeholder="+1 555 555 0123" data-rd-fit-phone><button class="button button--secondary" type="button" data-rd-fit-sms>Text Me Raw Dawg Updates</button></div><label class="rd-fit__marketing"><input type="checkbox" data-rd-fit-sms-consent><span>I agree to receive recurring automated marketing texts from Raw Dawg at this number. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP to cancel. See our <a href="/policies/terms-of-service" target="_blank" rel="noopener">Terms</a> and <a href="/policies/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.</span></label><p class="rd-fit__cart-message" id="RDFitSmsMessage" role="status" data-rd-fit-sms-message hidden></p></section>
            <p class="rd-fit__disclaimer">${this.replaceTokens(this.config.result?.disclaimer || "Estimated feeding guidance and ingredient matching. Puppies, pregnancy, nursing, medical conditions and prescribed diets may need a different plan; ask your veterinarian for individual advice.")}</p>
            <div class="rd-fit__result-actions"><button class="rd-fit__revise" type="button" aria-label="${escapeHtml(`${this.tokenText(this.config.labels?.back || "Back")} to revise answers`)}" data-rd-fit-revise>${this.replaceTokens(this.config.labels?.back || "Back")}<span class="visually-hidden"> to revise answers</span></button><button class="rd-fit__restart" type="button" data-rd-fit-restart>${this.replaceTokens(this.config.labels?.restart || "Check another dog")}</button></div>
          </div>
        </div>`;
      this.bindResultControls(result);
      if (focus) this.focusAfterRender("[data-rd-fit-view-title]");
    }

    supportedCadenceWeeks(value) {
      const cadence = Number(value);
      return [2, 4].includes(cadence) ? cadence : null;
    }

    availableBoxSizesForResult(result, cadenceWeeks = 4) {
      const role = this.resultRole(result);
      const product = this.getProduct(result);
      if (!role || !product || typeof this.core?.selectRecommendedSubscriptionVariant !== "function") return [];
      return [8, 14, 30].filter((size) => this.core.selectRecommendedSubscriptionVariant(
        role,
        size,
        product.variants || [],
        this.effectiveAvoidances(result),
        cadenceWeeks,
      )?.valid === true);
    }

    cadenceBoxPlan(result, cadenceWeeks) {
      const cadence = this.supportedCadenceWeeks(cadenceWeeks);
      if (!cadence) return null;
      if (cadence === 4) return result?.boxPlan || null;
      const saved = result?.cadenceBoxPlans?.[String(cadence)] || result?.cadenceBoxPlans?.[cadence]
        || result?.cadence_box_plans?.[String(cadence)] || result?.cadence_box_plans?.[cadence];
      if (saved?.quantities) return saved;
      if (typeof this.core?.calculateCadenceBoxPlan !== "function") return null;
      return this.core.calculateCadenceBoxPlan(
        result?.exactDailyStartingPounds,
        cadence,
        this.availableBoxSizesForResult(result, cadence),
      );
    }

    cadenceSelection(result, cadenceWeeks) {
      const cadence = this.supportedCadenceWeeks(cadenceWeeks);
      const role = this.resultRole(result);
      const boxPlan = this.cadenceBoxPlan(result, cadence);
      if (!cadence || !role || !boxPlan || typeof this.core?.verifyCadenceBoxPlanInventory !== "function") {
        return { valid: false, weeks: cadence, reason: "This subscription cadence could not be verified." };
      }
      let inventory;
      try {
        inventory = this.core.verifyCadenceBoxPlanInventory(
          role,
          boxPlan,
          this.effectiveAvoidances(result),
          this.recommendationOptions(),
          cadence,
        );
      } catch (_error) {
        return { valid: false, weeks: cadence, reason: "This subscription cadence could not be verified." };
      }
      if (!inventory?.valid || !inventory?.sellingPlan || !Array.isArray(inventory.selections) || !inventory.selections.length) {
        return { valid: false, weeks: cadence, reason: inventory?.reason || "This subscription cadence could not be verified." };
      }
      const linePlanIds = inventory.sellingPlan.linePlanIds || {};
      const variantSelections = inventory.selections.map((selection) => ({
        size: Number(selection.size),
        quantity: Number(selection.quantity),
        variantId: String(selection.variant?.id || ""),
        proteinComposition: selection.proteinComposition || [],
        sellingPlanId: String(linePlanIds[String(selection.size)] || inventory.sellingPlan.id || "") || null,
      }));
      if (variantSelections.some((selection) => !selection.variantId || !selection.sellingPlanId)) {
        return { valid: false, weeks: cadence, reason: "An exact subscription allocation could not be verified for every required box." };
      }
      const lines = inventory.selections.map((selection) => ({
        size: Number(selection.size),
        quantity: Number(selection.quantity),
        variant: selection.variant,
        sellingPlanId: String(linePlanIds[String(selection.size)] || inventory.sellingPlan.id || "") || null,
      }));
      const resultForCadence = {
        ...result,
        guardFingerprintSource: result?.guardFingerprintSource || result,
        purchaseAllowed: true,
        purchaseType: "subscription",
        purchaseCadenceWeeks: cadence,
        sellingPlan: cadence === 4 ? inventory.sellingPlan : result?.sellingPlan,
        cadenceBoxPlan: cadence === 2 ? boxPlan : null,
        cadenceSellingPlan: cadence === 2 ? inventory.sellingPlan : null,
        variantSelections,
      };
      const validation = this.validateCartLines(lines, resultForCadence);
      const exactPlans = lines.every((line) => {
        const plan = this.selectedSellingPlan(line, cadence, resultForCadence);
        return plan && String(plan.id) === String(line.sellingPlanId);
      });
      return {
        valid: validation?.valid === true && exactPlans,
        weeks: cadence,
        boxPlan,
        sellingPlan: inventory.sellingPlan,
        pricing: inventory.pricing || null,
        lines,
        result: resultForCadence,
        validation,
        reason: validation?.reason || (exactPlans ? null : "An exact subscription allocation could not be verified for every required box."),
      };
    }

    availableCadences(result) {
      return [4, 2]
        .map((cadence) => this.cadenceSelection(result, cadence))
        .filter((selection) => selection.valid === true)
        .sort((left, right) => Number(right.weeks) - Number(left.weeks));
    }

    preferredCadence(result, selections) {
      const recommended = this.supportedCadenceWeeks(result?.recommendedCadenceWeeks || result?.recommended_cadence_weeks);
      if (recommended && selections.some((selection) => selection.weeks === recommended)) return recommended;
      return selections.some((selection) => selection.weeks === 4) ? 4 : (selections[0]?.weeks || null);
    }

    purchaseSelectionFingerprint(result) {
      return JSON.stringify({
        role: this.resultRole(result),
        quantities: result?.boxPlan?.quantities || {},
        recommendedCadenceWeeks: result?.recommendedCadenceWeeks || result?.recommended_cadence_weeks || 4,
      });
    }

    resolvePurchaseSelection(result) {
      const selections = this.availableCadences(result);
      const fingerprint = this.purchaseSelectionFingerprint(result);
      const preferredCadence = this.preferredCadence(result, selections);
      const persistedCadence = this.supportedCadenceWeeks(this.state.cadence);
      const currentCadence = selections.find((selection) => selection.weeks === persistedCadence) || null;
      const isCurrentResult = this.state.purchaseSelectionFingerprint === fingerprint;
      if (!isCurrentResult || (this.state.purchaseType !== "one_time" && !currentCadence)) {
        this.state.purchaseType = "subscription";
        this.state.cadence = String(preferredCadence || 4);
        this.state.purchaseSelectionFingerprint = fingerprint;
      }
      const purchaseType = this.state.purchaseType === "one_time" ? "one_time" : "subscription";
      const selectedCadence = purchaseType === "subscription"
        ? (selections.find((selection) => selection.weeks === this.supportedCadenceWeeks(this.state.cadence))
          || selections.find((selection) => selection.weeks === preferredCadence)
          || null)
        : null;
      return { selections, purchaseType, selectedCadence, preferredCadence };
    }

    builderMarkup(result) {
      const selection = this.resolvePurchaseSelection(result);
      const fourWeek = selection.selections.find((plan) => plan.weeks === 4);
      const twoWeek = selection.selections.find((plan) => plan.weeks === 2);
      const blocked = !fourWeek;
      const guarded = ["wait", "conditional"].includes(result.status);
      const buttonLabel = guarded ? "I understand — order for later" : (this.config.labels?.add || "Add Recommended Box");
      const disabled = guarded && result.guardAcknowledged !== true;
      const selectedPurchaseValue = selection.purchaseType === "one_time"
        ? "one_time"
        : (selection.selectedCadence?.weeks === 2 ? "subscribe_2" : "subscribe");
      const heading = selection.purchaseType === "one_time"
        ? "One-time purchase"
        : `Every ${selection.selectedCadence?.weeks || 4} weeks · Subscribe & Save`;
      const description = selection.purchaseType === "one_time"
        ? "Order this 28-day box plan once, without a subscription."
        : selection.selectedCadence?.weeks === 2
          ? "Your selected box mix ships every two weeks. The 28-day estimate above remains the feeding reference."
          : "Delivery every four weeks is selected by default. A one-time purchase option is also available.";
      const pricing = selection.purchaseType === "one_time"
        ? (fourWeek?.pricing || result.displayPricing || result.pricing)
        : (selection.selectedCadence?.pricing || result.displayPricing || result.pricing);
      const currency = pricing?.currency || this.config.currency || "USD";
      const selectedPrice = selection.purchaseType === "one_time"
        ? pricing?.oneTimePriceCents
        : pricing?.subscriptionPriceCents;
      const savings = selection.purchaseType === "subscription" && Number(pricing?.savingsCents) > 0
        ? `Save ${moneyFromCents(pricing.savingsCents, currency)}${Number(pricing.savingsPercent) > 0 ? ` (${numberText(pricing.savingsPercent)}%)` : ""}`
        : "No subscription commitment";
      const plan = result.displayBoxPlan || selection.selectedCadence?.boxPlan || fourWeek?.boxPlan || result.boxPlan;
      const shortfall = Number(plan?.shortfallPounds28Days ?? plan?.shortfallPounds ?? 0);
      const coverageWarning = shortfall > 0.000001
        ? `<p class="rd-fit__coverage-warning"><strong>Plan the gap:</strong> ${escapeHtml(this.boxPlanMessage(plan))}</p>`
        : "";
      const priceMarkup = pricing && Number.isFinite(Number(selectedPrice))
        ? `<div class="rd-fit__purchase-price"><span>Selected order</span><strong>${escapeHtml(moneyFromCents(selectedPrice, currency))}</strong><small>${escapeHtml(savings)}</small></div>`
        : "";
      return `<div class="rd-fit__builder"><div class="rd-fit__builder-heading"><p class="rd-fit__eyebrow">Order the plan</p><h3>${escapeHtml(heading)}</h3><p>${escapeHtml(description)}</p></div><div class="rd-fit__purchase-controls"><div class="rd-fit__field"><label for="RDFitPurchaseType">Purchase option</label><select class="rd-fit__select" id="RDFitPurchaseType" data-rd-fit-purchase><option value="subscribe" ${selectedPurchaseValue === "subscribe" ? "selected" : ""} ${fourWeek ? "" : "disabled"}>Every 4 weeks · Subscribe &amp; Save</option><option value="subscribe_2" ${selectedPurchaseValue === "subscribe_2" ? "selected" : ""} ${twoWeek ? "" : "disabled"}>Every 2 weeks · Subscribe &amp; Save</option><option value="one_time" ${selectedPurchaseValue === "one_time" ? "selected" : ""}>One-time purchase</option></select></div>${priceMarkup}</div>${coverageWarning}${blocked ? `<p class="rd-fit__cart-message" data-state="error">This box is not currently available with the required delivery plan. Review your answers or contact us for help.</p>` : `<button class="button button--primary rd-fit__cart-button" type="button" aria-label="${this.replaceTokens(buttonLabel)}" data-rd-fit-add ${disabled ? "disabled" : ""}><span class="button__content"><span class="button__label" data-label="${this.replaceTokens(buttonLabel)}">${this.replaceTokens(buttonLabel)}</span></span><span class="animated-button-circle"></span></button><p class="rd-fit__cart-message" role="status" data-rd-fit-cart-message hidden></p>`}</div>`;
    }

    bindResultControls(result) {
      const purchase = this.stage.querySelector("[data-rd-fit-purchase]");
      const phoneInput = this.stage.querySelector("[data-rd-fit-phone]");
      const smsConsent = this.stage.querySelector("[data-rd-fit-sms-consent]");
      const smsMessage = this.stage.querySelector("[data-rd-fit-sms-message]");
      purchase?.addEventListener("change", () => {
        if (purchase.value === "subscribe_2") {
          this.state.purchaseType = "subscription";
          this.state.cadence = "2";
        } else if (purchase.value === "subscribe") {
          this.state.purchaseType = "subscription";
          this.state.cadence = "4";
        } else {
          this.state.purchaseType = "one_time";
        }
        this.saveState();
        this.renderResult({ focus: false });
      });
      this.stage.querySelector("[data-rd-fit-meals]")?.addEventListener("change", (event) => {
        this.state.mealsPerDay = Number(event.target.value) || 2;
        this.saveState();
        this.renderResult({ focus: false });
      });
      (this.stage.querySelectorAll?.("[data-rd-fit-choice-product]") || []).forEach((choice) => choice.addEventListener("click", () => {
        this.invalidateAnswerDerivedState();
        this.state.selectedProductRole = choice.dataset.rdFitChoiceProduct;
        this.state.selected_product_role = this.state.selectedProductRole;
        this.saveState();
        this.renderResult();
      }));
      this.stage.querySelector("[data-rd-fit-guard]")?.addEventListener("change", (event) => {
        this.state.guardAcknowledged = event.target.checked === true;
        this.state.guardAcknowledgementFingerprint = this.state.guardAcknowledged ? this.guardFingerprint(result) : null;
        this.saveState();
        const button = this.stage.querySelector("[data-rd-fit-add]");
        const current = this.guardIsCurrent(result);
        if (button) button.disabled = !current;
        this.trackQuizEvent("quiz_guard_ack", { guarded: current }, "quiz_guard_ack");
      });
      this.stage.querySelector("[data-rd-fit-add]")?.addEventListener("click", (event) => {
        const button = event.currentTarget;
        if (button.dataset.added === "true" || button.dataset.added === "review") {
          window.location.assign(`${window.Shopify?.routes?.root || "/"}cart`);
          return;
        }
        const currentGuardAcknowledged = this.guardIsCurrent(result)
          && this.stage.querySelector("[data-rd-fit-guard]")?.checked === true;
        const selected = this.resolvePurchaseSelection(result);
        const basePurchaseAllowed = result.purchaseAllowed === true
          || (["wait", "conditional"].includes(result.status) && currentGuardAcknowledged);
        let purchaseResult;
        if (selected.purchaseType === "one_time") {
          const fourWeek = selected.selections.find((selection) => selection.weeks === 4);
          if (!fourWeek) {
            this.setMessage(this.stage.querySelector("[data-rd-fit-cart-message]"), "The verified four-week box plan is unavailable for a one-time purchase.", "error");
            return;
          }
          const cartLines = fourWeek.lines.map((line) => ({ ...line, sellingPlanId: null }));
          purchaseResult = {
            ...result,
            guardFingerprintSource: result,
            purchaseAllowed: basePurchaseAllowed,
            purchaseType: "one_time",
            purchaseCadenceWeeks: null,
            cadenceBoxPlan: null,
            cadenceSellingPlan: null,
            variantSelections: fourWeek.result.variantSelections,
            cartLines,
          };
          purchaseResult.cartValidation = this.validateCartLines(cartLines, purchaseResult);
        } else {
          const cadence = selected.selectedCadence;
          if (!cadence?.valid || !cadence?.result || !cadence?.validation?.valid) {
            this.setMessage(this.stage.querySelector("[data-rd-fit-cart-message]"), "The selected subscription cadence could not be verified. Choose another purchase option.", "error");
            return;
          }
          purchaseResult = {
            ...cadence.result,
            guardFingerprintSource: result,
            purchaseAllowed: basePurchaseAllowed,
            cartValidation: cadence.validation,
            cartLines: cadence.lines,
          };
        }
        this.addRecommendedBox(purchaseResult, event);
      });
      this.stage.querySelector("[data-rd-fit-sms]")?.addEventListener("click", () => this.saveSmsOptIn());
      phoneInput?.addEventListener("input", () => this.clearFieldError(phoneInput, smsMessage));
      smsConsent?.addEventListener("change", () => this.clearFieldError(smsConsent, smsMessage));
      this.stage.querySelectorAll?.("[data-rd-fit-edit-result]")?.forEach((button) => button.addEventListener("click", () => this.editResultSection(button.dataset.rdFitEditResult)));
      this.stage.querySelector("[data-rd-fit-revise]")?.addEventListener("click", () => this.reviseAnswers());
      this.stage.querySelector("[data-rd-fit-restart]")?.addEventListener("click", () => this.restart());
    }

    setFieldError(field, message, text) {
      this.setMessage(message, text, "error");
      if (!field || !message?.id) return;
      field.setAttribute("aria-invalid", "true");
      field.setAttribute("aria-describedby", message.id);
      field.focus({ preventScroll: true });
    }

    clearFieldError(field, message) {
      if (!field || !message?.id || field.getAttribute?.("aria-describedby") !== message.id) return;
      field.removeAttribute("aria-invalid");
      field.removeAttribute("aria-describedby");
      if (message.dataset?.state === "error") this.setMessage(message, "");
    }

    setMessage(element, text, state = "") {
      if (!element) return;
      element.textContent = text;
      element.hidden = !text;
      if (state) element.dataset.state = state;
      else delete element.dataset.state;
      if (state === "error") element.setAttribute?.("role", "alert");
      else if (text) element.setAttribute?.("role", "status");
    }

    selectedSellingPlan(line, cadence, result = null) {
      const cadenceWeeks = this.supportedCadenceWeeks(cadence);
      if (!cadenceWeeks) return null;
      const plans = line.variant?.sellingPlans || [];
      const expected = toArray(result?.variantSelections).find((selection) => (
        Number(selection?.size) === Number(line?.size)
        && (!selection?.variantId || String(selection.variantId) === String(line?.variant?.id))
      ));
      const cadencePlan = cadenceWeeks === 2 ? result?.cadenceSellingPlan : result?.sellingPlan;
      const requestedIds = [
        line?.sellingPlanId,
        expected?.sellingPlanId,
        cadencePlan?.linePlanIds?.[String(line?.size)],
        cadencePlan?.id,
      ].map((id) => String(id || "")).filter(Boolean);
      for (const requestedId of requestedIds) {
        const exact = plans.find((plan) => (
          String(plan?.id) === requestedId
          && this.cadenceWeeks(plan) === String(cadenceWeeks)
        ));
        if (exact) return exact;
      }
      return plans.find((plan) => this.cadenceWeeks(plan) === String(cadenceWeeks)) || null;
    }

    cadenceWeeks(plan) {
      if (typeof this.core?.planIsEveryCadenceWeeks === "function") {
        for (const cadence of [2, 4]) {
          if (this.core.planIsEveryCadenceWeeks(plan, cadence)) return String(cadence);
        }
        return null;
      }
      const match = String(plan?.name || "").match(/(?:^|[^\d])(?:every\s+)?([2468])\s*weeks?(?=$|[^\d])/i);
      return match ? match[1] : null;
    }

    reviseAnswers() {
      this.invalidateAnswerDerivedState();
      this.state.view = "question";
      this.state.currentView = "question";
      this.state.step = TOTAL_STEPS - 1;
      this.saveState();
      this.pushHistory(true);
      this.renderCurrent();
    }

    createCartAddId() {
      const nonce = typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      return `rd-fit-add-${nonce}`;
    }

    cartLineProperties(addId, index) {
      return {
        "_rd_fit_line_id": `${addId}-${index + 1}`,
      };
    }

    async readCart(root) {
      try {
        const response = await fetch(`${root}cart.js`, { headers: { Accept: "application/json" } });
        if (!response.ok) return { valid: false, reason: "The current cart could not be verified." };
        const cart = await response.json();
        if (!Array.isArray(cart?.items)) return { valid: false, reason: "The current cart could not be verified." };
        return { valid: true, cart };
      } catch (_error) {
        return { valid: false, reason: "The current cart could not be verified." };
      }
    }

    cartItemMatchesAddedLine(cartItem, item) {
      if (String(cartItem?.variant_id) !== String(item.id) || Number(cartItem?.quantity) !== Number(item.quantity)) return false;
      const expectedProperties = item.properties || {};
      const actualProperties = cartItem?.properties && typeof cartItem.properties === "object" ? cartItem.properties : {};
      const expectedKeys = Object.keys(expectedProperties);
      if (Object.keys(actualProperties).length !== expectedKeys.length) return false;
      if (!expectedKeys.every((key) => String(actualProperties[key]) === String(expectedProperties[key]))) return false;
      if (!item.selling_plan) return !cartItem.selling_plan_allocation;
      return String(cartItem.selling_plan_allocation?.selling_plan?.id) === String(item.selling_plan);
    }

    async verifyCartAddition(items, beforeCart, root) {
      const beforeItems = Array.isArray(beforeCart?.items) ? beforeCart.items : [];
      const lineIds = items.map((item) => item.properties?.["_rd_fit_line_id"]);
      if (!lineIds.length || lineIds.some((lineId) => !lineId) || new Set(lineIds).size !== lineIds.length) {
        return { valid: false, reason: "The added box could not be verified in the cart." };
      }
      if (items.some((item) => beforeItems.some((cartItem) => (
        String(cartItem?.properties?.["_rd_fit_line_id"] || "") === String(item.properties?.["_rd_fit_line_id"] || "")
      )))) {
        return { valid: false, reason: "The added box could not be verified in the cart." };
      }
      const after = await this.readCart(root);
      if (!after.valid) return { valid: false, reason: "The added box could not be verified in the cart." };
      const matchedIndexes = new Set();
      const valid = items.every((item) => {
        const index = after.cart.items.findIndex((cartItem, cartIndex) => (
          !matchedIndexes.has(cartIndex) && this.cartItemMatchesAddedLine(cartItem, item)
        ));
        if (index < 0) return false;
        matchedIndexes.add(index);
        return true;
      });
      return valid
        ? { valid: true, cart: after.cart }
        : { valid: false, reason: "The added box could not be verified in the cart." };
    }

    markCartForReview(button) {
      this.setButtonLabel(button, "Review cart");
      button.dataset.added = "review";
    }

    async refreshCart(event, parsedState) {
      const cart = document.querySelector("cart-drawer");
      if (typeof cart?.renderContents === "function") {
        const activeElement = this.inline ? document.activeElement : this.lastFocused || document.activeElement;
        if (!this.inline && this.isOpen) this.close();
        cart.setActiveElement?.(activeElement);
        await cart.renderContents(parsedState, event);
        cart.classList.toggle("is-empty", Boolean(cart.querySelector(".drawer__inner-empty")));
      }
      if (typeof CustomEvent === "function") document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }

    async addRecommendedBox(result, event) {
      const button = this.stage.querySelector("[data-rd-fit-add]");
      const message = this.stage.querySelector("[data-rd-fit-cart-message]");
      if (!button || button.disabled || !result.purchaseAllowed || result.cartValidation?.valid !== true) return;
      const currentGuardAcknowledged = this.guardIsCurrent(result)
        && this.stage.querySelector("[data-rd-fit-guard]")?.checked === true;
      if (["wait", "conditional"].includes(result.status) && !currentGuardAcknowledged) {
        this.setMessage(message, "Acknowledge the future-plan note before ordering for later.", "error");
        return;
      }

      const lines = result.cartLines || [];
      const preflight = this.validateCartLines(lines, result);
      if (!preflight.valid) {
        this.setMessage(message, preflight.reason || "We could not verify a compatible box for the selected protein avoidances.", "error");
        return;
      }

      const subscription = result.purchaseType === "subscription";
      const cadence = subscription ? String(result.purchaseCadenceWeeks || this.state.cadence || "4") : null;
      const planMatchesLine = (line) => {
        const plan = this.selectedSellingPlan(line, cadence, result);
        return plan && String(plan.id) === String(line.sellingPlanId || "");
      };
      if (subscription && lines.some((line) => !planMatchesLine(line))) {
        this.setMessage(message, `Every ${cadence} weeks is not available for every box in this recommendation. Choose another cadence or a one-time purchase.`, "error");
        return;
      }

      const addId = this.createCartAddId();
      const items = lines.map((line, index) => {
        const item = {
          id: line.variant.id,
          quantity: line.quantity,
          properties: this.cartLineProperties(addId, index),
        };
        if (subscription) item.selling_plan = this.selectedSellingPlan(line, cadence, result).id;
        return item;
      });
      const finalCartValidation = this.validateCartLines(lines.map((line, index) => ({
        ...line,
        sellingPlanId: items[index].selling_plan || null,
      })), result);
      if (!finalCartValidation.valid) {
        this.setMessage(message, finalCartValidation.reason || "The selected box could not be verified for cart addition.", "error");
        return;
      }

      const root = window.Shopify?.routes?.root || "/";
      const before = await this.readCart(root);
      if (!before.valid) {
        this.setMessage(message, before.reason || "The current cart could not be verified. Please try again.", "error");
        return;
      }

      button.disabled = true;
      let requestDispatched = false;
      let serverRejected = false;
      try {
        const cart = document.querySelector("cart-drawer");
        const sections = typeof cart?.getSectionsToRender === "function"
          ? cart.getSectionsToRender().map((section) => section.id).slice(0, 5)
          : [];
        const requestBody = { items };
        if (sections.length) {
          requestBody.sections = sections;
          requestBody.sections_url = window.location.pathname;
        }
        requestDispatched = true;
        const response = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(requestBody),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          serverRejected = true;
          throw new Error(data.description || "The box could not be added.");
        }

        const verification = await this.verifyCartAddition(items, before.cart, root);
        if (!verification.valid) {
          this.setMessage(message, "We could not verify the newly added box in your cart. Review your cart before trying again.", "error");
          this.markCartForReview(button);
          await this.refreshCart(event, data);
          return;
        }
        this.setMessage(message, this.tokenText(this.config.labels?.added || "Your Raw Dawg box is in the cart."), "success");
        await this.refreshCart(event, data);
        this.setButtonLabel(button, "View cart");
        button.dataset.added = "true";
        this.trackQuizEvent("quiz_product_added", {
          recommendation: this.resultRole(result) || "unknown",
          purchase_type: subscription ? "subscription" : "one_time",
          cadence_weeks: subscription ? Number(cadence) : null,
          box_count: items.reduce((total, item) => total + Number(item.quantity || 0), 0),
        }, "quiz_product_added");
      } catch (error) {
        if (requestDispatched && !serverRejected) {
          this.setMessage(message, "We could not confirm whether the box was added. Review your cart before trying again.", "error");
          this.markCartForReview(button);
          await this.refreshCart(event);
        } else {
          this.setMessage(message, error.message || "The box could not be added. Please try again.", "error");
        }
      } finally {
        button.disabled = false;
      }
    }

    stripDiagnosticHealthDetail(payload) {
      if (!payload || typeof payload !== "object") return payload;
      const blocked = /(^|_)(health|condition|diagnos|medical)(_|$)/i;
      const preserve = new Set(["qualification_category", "qualificationCategory"]);
      if (Array.isArray(payload)) return payload.map((item) => this.stripDiagnosticHealthDetail(item));
      return Object.entries(payload).reduce((safe, [key, value]) => {
        if (blocked.test(key) && !preserve.has(key)) return safe;
        safe[key] = this.stripDiagnosticHealthDetail(value);
        return safe;
      }, {});
    }

    assertProviderPayload(payload) {
      const completionId = payload?.completion_id || payload?.completionId;
      const schemaVersion = payload?.schema_version || payload?.schemaVersion;
      const dogId = payload?.dog_id || payload?.dogId;
      if (!completionId || !schemaVersion || !dogId) throw new Error("The plan-email contract is incomplete. Please try again later.");
    }

    provider() {
      return typeof window.RawDawgQuizEventProvider === "function" ? window.RawDawgQuizEventProvider : null;
    }

    deliveryState() {
      this.state.eventAcknowledgements = this.state.eventAcknowledgements || {};
      const id = this.state.completionId || this.state.completion_id;
      this.state.eventAcknowledgements[id] = this.state.eventAcknowledgements[id] || {};
      return this.state.eventAcknowledgements[id];
    }

    async acknowledgeEvent(name, payload) {
      const provider = this.provider();
      if (!provider) throw new Error("A plan-email provider is not available yet.");
      const acknowledgement = await provider({ event: name, payload });
      if (acknowledgement?.acknowledged !== true) throw new Error("The provider did not acknowledge the request.");
      return acknowledgement;
    }

    async saveEmailPlan(result) {
      const emailInput = this.stage.querySelector("[data-rd-fit-email]");
      const email = emailInput?.value.trim();
      const firstName = this.stage.querySelector("[data-rd-fit-first-name]")?.value.trim() || "";
      const button = this.stage.querySelector("[data-rd-fit-send-results]");
      const message = this.stage.querySelector("[data-rd-fit-email-message]");
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        this.setFieldError(emailInput, message, "Add a valid email address.");
        return;
      }
      this.clearFieldError(emailInput, message);
      // Recheck at the send boundary instead of trusting the render-time closure.
      const options = this.recommendationOptions();
      for (let step = 0; step < TOTAL_STEPS; step += 1) {
        const answer = this.core.evaluateQuestionState(this.state, step, options);
        if (!answer.complete) {
          this.state.view = this.state.currentView = "question";
          this.state.step = step;
          this.state.returnToEmailAfterEdit = true;
          this.state.emailEditStep = step;
          this.saveState();
          this.pushHistory(true);
          this.renderCurrent();
          return;
        }
      }
      this.persistAgeMetadata();
      result = this.evaluateCartReadiness(this.core.calculateResult(this.state, options));
      this.state.result = result;
      this.state.email = email;
      this.state.firstName = firstName;
      this.state.emailConsent = true;
      this.state.emailMarketingConsent = true;
      this.ensureCompletionId();
      if (!this.state.completionSnapshotAt) this.state.completionSnapshotAt = new Date().toISOString();
      const consentTimestamp = new Date().toISOString();
      const payloadOptions = {
        email,
        firstName,
        emailConsent: true,
        marketingConsent: true,
        emailMarketingConsent: true,
        sendMyResults: true,
        identificationConfirmed: true,
        consentSource: "raw_fit_email_step_send_results",
        consentWordingVersion: "raw-fit-v3-email-consent-1",
        completedAt: this.state.completionSnapshotAt,
        consentTimestamp,
      };
      const completionPayload = this.core.buildCompletionPayload(this.state, result, payloadOptions);
      const canonicalSnapshot = completionPayload.properties?.completion_snapshot;
      const planPayload = this.core.buildPlanEmailPayload(this.state, result, {
        ...payloadOptions,
        completionSnapshot: canonicalSnapshot,
      });
      const explicitEmailConsent = {
        send_results: true,
        email_marketing: true,
        source: payloadOptions.consentSource,
        wording_version: payloadOptions.consentWordingVersion,
        timestamp: consentTimestamp,
      };
      completionPayload.consent = { ...(completionPayload.consent || {}), ...explicitEmailConsent };
      planPayload.consent = { ...(planPayload.consent || {}), ...explicitEmailConsent };
      try {
        this.assertProviderPayload(completionPayload);
        this.assertProviderPayload(planPayload);
      } catch (error) {
        this.setMessage(message, error.message, "error");
        return;
      }

      const requestCompletionId = this.state.completionId;
      const requestDogId = this.state.dogId;
      const isCurrentRequest = () => this.state.completionId === requestCompletionId
        && this.state.dogId === requestDogId && this.state.view === "email";
      button.disabled = true;
      try {
        const delivery = this.deliveryState();
        if (completionPayload.shouldTransmit === true && !delivery.quizCompleted) {
          await this.acknowledgeEvent("Raw Fit Quiz Completed", completionPayload);
          if (!isCurrentRequest()) return;
          delivery.quizCompleted = true;
          this.saveState();
        }
        if (!delivery.planEmailRequested) {
          await this.acknowledgeEvent("Raw Fit Plan Requested", planPayload);
          if (!isCurrentRequest()) return;
          delivery.planEmailRequested = true;
          this.saveState();
        }
        if (!isCurrentRequest()) return;
        this.state.planRequestAcknowledged = true;
        this.setMessage(message, "Your plan request was accepted. Watch your inbox for the plan and $10 offer.", "success");
        this.trackQuizEvent("quiz_email_submit", { email_step: "submit" }, "quiz_email_submit");
        this.state.view = "result";
        this.state.currentView = "result";
        this.saveState();
        this.pushHistory();
        this.renderCurrent();
      } catch (_error) {
        if (!isCurrentRequest()) return;
        this.setMessage(message, "We could not confirm that your plan was saved. Your answers remain in this browser. Please try again.", "error");
      } finally {
        button.disabled = false;
      }
    }

    normalizePhone(value) {
      const raw = String(value || "").trim();
      const digits = raw.replace(/\D/g, "");
      if (raw.startsWith("+") && digits.length >= 8 && digits.length <= 15) return `+${digits}`;
      if (digits.length === 10) return `+1${digits}`;
      if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
      return null;
    }

    async saveSmsOptIn() {
      const phoneInput = this.stage.querySelector("[data-rd-fit-phone]");
      const phone = this.normalizePhone(phoneInput?.value);
      const smsConsent = this.stage.querySelector("[data-rd-fit-sms-consent]");
      const consent = smsConsent?.checked === true;
      const message = this.stage.querySelector("[data-rd-fit-sms-message]");
      if (!phone) {
        this.setFieldError(phoneInput, message, "Add a valid phone number with country code (for example, +1 555 555 0123).");
        return;
      }
      if (phoneInput) phoneInput.value = phone;
      if (!consent) {
        this.setFieldError(smsConsent, message, "Check the SMS consent box if you want text updates.");
        return;
      }
      this.clearFieldError(phoneInput, message);
      this.clearFieldError(smsConsent, message);
      this.ensureCompletionId();
      const payload = typeof this.core.buildSmsOptInPayload === "function"
        ? this.core.buildSmsOptInPayload(this.state, this.state.result || {}, {
          phone,
          smsMarketingConsent: true,
          consentSource: "raw_fit_results_sms",
          consentWordingVersion: "raw-fit-v3.4-sms-consent-2",
        })
        : {
          schema_version: String(this.core.SCHEMA_VERSION),
          completion_id: String(this.state.completionId || this.state.completion_id || ""),
          dog_id: String(this.state.dogId || this.state.dog_id || ""),
          phone,
          sms_consent: true,
          consent_source: "raw_fit_results_sms",
          wording_version: "raw-fit-v3.4-sms-consent-2",
        };
      const provider = this.provider();
      if (!provider) {
        this.setMessage(message, "SMS signup is not available right now. Your results are still available.", "error");
        return;
      }
      try {
        await this.acknowledgeEvent("Raw Fit SMS Opt-In", payload);
        this.state.phone = phone;
        this.state.smsConsent = true;
        this.saveState();
        this.setMessage(message, "You’re on the Raw Dawg SMS list.", "success");
        this.trackQuizEvent("quiz_sms_opt_in", { sms_opt_in: true }, "quiz_sms_opt_in");
      } catch (_error) {
        this.setMessage(message, "We could not confirm the SMS signup. Please try again.", "error");
      }
    }

    restart() {
      this.trackQuizEvent("quiz_restarted");
      this.state = this.defaultState();
      this.ensureDogId();
      this.saveState();
      this.pushHistory(true);
      this.renderCurrent();
    }
  }

  window.RawDawgFitQuizClass = RawDawgFitQuiz;
  if (!customElements.get("rd-fit-quiz")) customElements.define("rd-fit-quiz", RawDawgFitQuiz);
})();
